import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  SignedIn,
  SignedOut,
  SignInButton,
  SignUpButton,
  UserButton,
  useUser,
  useAuth,
} from '@clerk/clerk-react';
import { detectFileType, convertVideoToAudio } from './videoConverter';
import HelpPage from './HelpPage';
import FeedbackPage from './FeedbackPage';
import LandingPage from './LandingPage';
import { toUserMessage } from './errorMessages';
import './App.css';

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const BUILD_TAG = '2026-07-15b';

/** Document translation jobs — includes legacy localStorage entries saved as jobType 'pdf'. */
function isDocumentJob(job) {
  return job.jobType === 'document' || (job.jobType === 'pdf' && job.targetLanguages?.length);
}

// ---------------------------------------------------------------------------
// Duration normalisation
//
// `job.processingTime` and `job.audioDuration` are ALWAYS SECONDS in this app.
// Raw transcript payloads are not: worker containers write
// processing_time_min/audio_duration_min in MINUTES, while docker/whisperx_api
// writes processing_time/duration in SECONDS. Never read those fields with a
// bare `??` chain — route them through these helpers instead.
// ---------------------------------------------------------------------------

function toNumber(value) {
  if (value == null || typeof value === 'boolean') return null;
  const num = Number(value);
  return Number.isFinite(num) && num >= 0 ? num : null;
}

function secondsFrom(doc, minuteKeys, secondKeys) {
  if (!doc) return null;
  for (const key of minuteKeys) {
    const minutes = toNumber(doc[key]);
    if (minutes != null) return Math.round(minutes * 60 * 100) / 100;
  }
  for (const key of secondKeys) {
    const seconds = toNumber(doc[key]);
    if (seconds != null) return Math.round(seconds * 100) / 100;
  }
  return null;
}

/** Processing time in seconds from a transcript or status payload. */
function processingSeconds(doc) {
  return secondsFrom(
    doc,
    ['processing_time_min', 'processingTimeMin'],
    ['processingTimeSec', 'processing_time', 'processingTime'],
  );
}

/** Audio duration in seconds from a transcript or status payload. */
function audioSeconds(doc) {
  return secondsFrom(
    doc,
    ['audio_duration_min', 'audioDurationMin'],
    ['audioDurationSec', 'duration', 'audio_duration', 'audioDuration'],
  );
}

// Finds the single changed phrase between two strings.
// Returns { original, corrected } or null if the change is too large or non-existent.
function diffWords(before, after) {
  if (!before || !after || before === after) return null;
  const norm = w => w.toLowerCase().replace(/[.,;:!?'"""''()[\]]/g, '');
  const bw = before.trim().split(/\s+/);
  const aw = after.trim().split(/\s+/);
  let s = 0;
  while (s < bw.length && s < aw.length && norm(bw[s]) === norm(aw[s])) s++;
  let be = bw.length - 1, ae = aw.length - 1;
  while (be >= s && ae >= s && norm(bw[be]) === norm(aw[ae])) { be--; ae--; }
  const original = bw.slice(s, be + 1).join(' ');
  const corrected = aw.slice(s, ae + 1).join(' ');
  if (!original.trim() || !corrected.trim()) return null;
  if (original.split(/\s+/).length > 6) return null; // skip whole-paragraph rewrites
  return { original: original.trim(), corrected: corrected.trim() };
}

// ============================================
// Translation Language Registry
// ============================================
const TRANSLATION_LANGUAGES = [
  // European — matches backend SUPPORTED_LANGS
  { code: 'fr', name: 'French',      region: 'European' },
  { code: 'de', name: 'German',      region: 'European' },
  { code: 'es', name: 'Spanish',     region: 'European' },
  { code: 'pt', name: 'Portuguese',  region: 'European' },
  { code: 'it', name: 'Italian',     region: 'European' },
  { code: 'nl', name: 'Dutch',       region: 'European' },
  { code: 'pl', name: 'Polish',      region: 'European' },
  { code: 'ro', name: 'Romanian',    region: 'European' },
  { code: 'uk', name: 'Ukrainian',   region: 'European' },
  // African
  { code: 'sw', name: 'Swahili',     region: 'African' },
  { code: 'yo', name: 'Yoruba',      region: 'African' },
  { code: 'ig', name: 'Igbo',        region: 'African' },
  { code: 'ha', name: 'Hausa',       region: 'African' },
  { code: 'am', name: 'Amharic',     region: 'African' },
  { code: 'zu', name: 'Zulu',        region: 'African' },
  // Asian & Middle East
  { code: 'zh', name: 'Chinese',     region: 'Asian' },
  { code: 'ja', name: 'Japanese',    region: 'Asian' },
  { code: 'ko', name: 'Korean',      region: 'Asian' },
  { code: 'hi', name: 'Hindi',       region: 'Asian' },
  { code: 'ar', name: 'Arabic',      region: 'Asian' },
];

const LANG_BY_REGION = TRANSLATION_LANGUAGES.reduce((acc, lang) => {
  (acc[lang.region] = acc[lang.region] || []).push(lang);
  return acc;
}, {});

const LANG_MAP = Object.fromEntries(TRANSLATION_LANGUAGES.map(l => [l.code, l.name]));

// ============================================
// WAV Encoding Utility
// ============================================
function encodeWAV(audioBuffer, startSample, endSample) {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const length = endSample - startSample;
  const buffer = new ArrayBuffer(44 + length * numChannels * 2);
  const view = new DataView(buffer);

  const writeString = (offset, str) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + length * numChannels * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, length * numChannels * 2, true);

  const channels = [];
  for (let ch = 0; ch < numChannels; ch++) {
    channels.push(audioBuffer.getChannelData(ch));
  }

  let offset = 44;
  for (let i = startSample; i < endSample; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, channels[ch][i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      offset += 2;
    }
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

// ============================================
// Trim Modal Component
// ============================================
function TrimModal({ file, onTrimComplete, onCancel }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const modalRef = useRef(null);
  const audioElRef = useRef(null);
  const blobUrlRef = useRef(null);
  const animFrameRef = useRef(null);
  const peaksRef = useRef(null);
  const playEndRef = useRef(0);
  const justDraggedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [duration, setDuration] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [dragging, setDragging] = useState(null);
  const [dragOrigin, setDragOrigin] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [playMode, setPlayMode] = useState('selection');

  // Setup audio element — zero memory waveform (no file reads at all)
  useEffect(() => {
    let cancelled = false;
    const url = URL.createObjectURL(file);
    blobUrlRef.current = url;
    const audio = new Audio();
    audio.preload = 'metadata';
    audio.src = url;
    audioElRef.current = audio;

    const init = async () => {
      try {
        setLoadProgress(20);
        // Get duration from audio element metadata only
        await new Promise((resolve, reject) => {
          if (audio.readyState >= 1 && isFinite(audio.duration)) return resolve();
          audio.addEventListener('loadedmetadata', () => {
            if (isFinite(audio.duration)) resolve();
            else reject(new Error('Could not determine audio duration'));
          }, { once: true });
          audio.addEventListener('error', () => reject(audio.error || new Error('Audio load error')), { once: true });
        });
        if (cancelled) return;
        const dur = audio.duration;
        setDuration(dur);
        setTrimEnd(dur);
        setLoadProgress(60);

        // Generate a natural-looking synthetic waveform — zero file reads, zero decode
        // Uses seeded PRNG for consistent look per file
        const NUM_PEAKS = 2000;
        const peaks = [];
        let seed = file.size ^ (dur * 1000);
        const rand = () => { seed = (seed * 16807 + 0) % 2147483647; return seed / 2147483647; };
        let amp = 0.3 + rand() * 0.3;
        for (let i = 0; i < NUM_PEAKS; i++) {
          // Slow envelope + medium variation + fast jitter
          const env = 0.3 + 0.5 * Math.sin(i * Math.PI / NUM_PEAKS);
          amp += (rand() - 0.5) * 0.08;
          amp = Math.max(0.1, Math.min(0.9, amp));
          const jitter = 0.7 + rand() * 0.3;
          const level = env * amp * jitter;
          peaks.push({ min: -level, max: level });
        }
        peaksRef.current = peaks;

        setLoadProgress(100);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setLoadError(toUserMessage(e, 'default'));
      }
    };
    init();

    return () => {
      cancelled = true;
      audio.pause();
      audio.src = '';
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      cancelAnimationFrame(animFrameRef.current);
    };
  }, [file, onCancel]);

  // Draw waveform
  useEffect(() => {
    if (loading || !peaksRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const peaks = peaksRef.current;
    const dpr = window.devicePixelRatio || 1;

    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;
    const mid = h / 2;
    const barWidth = w / peaks.length;

    // Background
    ctx.fillStyle = '#F8F5F0';
    ctx.fillRect(0, 0, w, h);

    // Center line
    ctx.strokeStyle = '#E5DFD6';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, mid);
    ctx.lineTo(w, mid);
    ctx.stroke();

    const startX = (trimStart / duration) * w;
    const endX = (trimEnd / duration) * w;

    // Draw dimmed waveform (outside selection)
    for (let i = 0; i < peaks.length; i++) {
      const x = i * barWidth;
      const { min, max } = peaks[i];
      const topH = max * mid * 0.85;
      const bottomH = -min * mid * 0.85;
      const isInSelection = x >= startX && x <= endX;

      if (!isInSelection) {
        ctx.fillStyle = '#D4C9BB';
        // Top bar
        const barW = Math.max(1, barWidth - 0.5);
        ctx.fillRect(x, mid - topH, barW, topH);
        // Bottom bar
        ctx.fillRect(x, mid, barW, bottomH);
      }
    }

    // Draw selected waveform with gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, h);
    gradient.addColorStop(0, '#B07840');
    gradient.addColorStop(0.4, '#8B5A2B');
    gradient.addColorStop(0.6, '#8B5A2B');
    gradient.addColorStop(1, '#B07840');

    for (let i = 0; i < peaks.length; i++) {
      const x = i * barWidth;
      const { min, max } = peaks[i];
      const topH = max * mid * 0.85;
      const bottomH = -min * mid * 0.85;
      const isInSelection = x >= startX && x <= endX;

      if (isInSelection) {
        ctx.fillStyle = gradient;
        const barW = Math.max(1, barWidth - 0.5);
        ctx.fillRect(x, mid - topH, barW, topH);
        ctx.fillRect(x, mid, barW, bottomH);
      }
    }

    // Dim overlay outside selection
    ctx.fillStyle = 'rgba(248, 245, 240, 0.45)';
    ctx.fillRect(0, 0, startX, h);
    ctx.fillRect(endX, 0, w - endX, h);

    // Selection boundary glow
    ctx.shadowColor = 'rgba(139, 90, 43, 0.3)';
    ctx.shadowBlur = 6;
    ctx.strokeStyle = '#8B5A2B';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(startX, 0);
    ctx.lineTo(startX, h);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(endX, 0);
    ctx.lineTo(endX, h);
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Playhead
    if (playbackTime > 0 && isPlaying) {
      const playX = (playbackTime / duration) * w;
      ctx.strokeStyle = '#E74C3C';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(playX, 0);
      ctx.lineTo(playX, h);
      ctx.stroke();
      ctx.setLineDash([]);

      // Playhead dot
      ctx.fillStyle = '#E74C3C';
      ctx.beginPath();
      ctx.arc(playX, 6, 4, 0, Math.PI * 2);
      ctx.fill();
    }

  }, [loading, trimStart, trimEnd, duration, playbackTime, isPlaying]);

  // Playback animation loop — tracks audio element time
  useEffect(() => {
    if (!isPlaying) {
      cancelAnimationFrame(animFrameRef.current);
      return;
    }
    const tick = () => {
      const audio = audioElRef.current;
      if (!audio || audio.paused) {
        setIsPlaying(false);
        setPlaybackTime(0);
        return;
      }
      const current = audio.currentTime;
      if (current >= playEndRef.current) {
        audio.pause();
        setIsPlaying(false);
        setPlaybackTime(0);
        return;
      }
      setPlaybackTime(current);
      animFrameRef.current = requestAnimationFrame(tick);
    };
    animFrameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [isPlaying]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (loading) return;
      // Don't capture keys when user is typing in an input
      if (e.target.tagName === 'INPUT') return;
      if (e.code === 'Space') {
        e.preventDefault();
        if (isPlaying) stopPlayback();
        else { setPlayMode('selection'); playAudio(trimStart, trimEnd); }
      } else if (e.code === 'Escape') {
        stopPlayback();
        onCancel();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [loading, isPlaying, trimStart, trimEnd]);

  // Focus trap — move focus into the modal on open, cycle Tab within it,
  // and return focus to whatever triggered the modal on close.
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const getFocusable = () =>
      Array.from(modalRef.current?.querySelectorAll(focusableSelector) || []).filter(
        (el) => !el.disabled && el.offsetParent !== null
      );

    const first = getFocusable()[0];
    (first || modalRef.current)?.focus();

    const handleTab = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const firstEl = focusable[0];
      const lastEl = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    window.addEventListener('keydown', handleTab);
    return () => {
      window.removeEventListener('keydown', handleTab);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, []);

  const stopPlayback = useCallback(() => {
    const audio = audioElRef.current;
    if (audio) audio.pause();
    setIsPlaying(false);
  }, []);

  const scrubAudio = useCallback((time) => {
    // Seek only — no playback during drag to keep memory safe
    const audio = audioElRef.current;
    if (audio) audio.currentTime = time;
    setPlaybackTime(time);
  }, []);

  const playAudio = useCallback((fromTime, toTime) => {
    stopPlayback();
    const audio = audioElRef.current;
    if (!audio) return;
    audio.currentTime = fromTime;
    playEndRef.current = toTime;
    audio.play().catch(() => {});
    setIsPlaying(true);
    setPlaybackTime(fromTime);
  }, [stopPlayback]);

  // Pointer handling
  const getTimeFromX = useCallback((clientX) => {
    if (!containerRef.current || !duration) return 0;
    const rect = containerRef.current.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) * duration;
  }, [duration]);

  const handlePointerDown = useCallback((e, type) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPlaying) stopPlayback();
    setDragging(type);
    if (type === 'region') {
      setDragOrigin({ x: e.clientX, start: trimStart, end: trimEnd });
    }
  }, [trimStart, trimEnd, isPlaying, stopPlayback]);

  const handlePointerMove = useCallback((e) => {
    if (!dragging) return;
    const time = getTimeFromX(e.clientX);
    if (dragging === 'start') {
      const t = Math.max(0, Math.min(time, trimEnd - 0.5));
      setTrimStart(t);
      scrubAudio(t);
    } else if (dragging === 'end') {
      const t = Math.min(duration, Math.max(time, trimStart + 0.5));
      setTrimEnd(t);
      scrubAudio(t);
    } else if (dragging === 'region' && dragOrigin) {
      const dx = e.clientX - dragOrigin.x;
      const rect = containerRef.current.getBoundingClientRect();
      const dt = (dx / rect.width) * duration;
      const regionLen = dragOrigin.end - dragOrigin.start;
      let newStart = dragOrigin.start + dt;
      let newEnd = dragOrigin.end + dt;
      if (newStart < 0) { newStart = 0; newEnd = regionLen; }
      if (newEnd > duration) { newEnd = duration; newStart = duration - regionLen; }
      setTrimStart(newStart);
      setTrimEnd(newEnd);
    }
  }, [dragging, trimStart, trimEnd, duration, getTimeFromX, dragOrigin, scrubAudio]);

  const handlePointerUp = useCallback(() => {
    if (dragging) {
      justDraggedRef.current = true;
      // Play a brief audio snippet at the final handle position
      const audio = audioElRef.current;
      if (audio) {
        const t = dragging === 'start' ? trimStart : dragging === 'end' ? trimEnd : trimStart;
        audio.currentTime = t;
        playEndRef.current = t + 0.2;
        audio.play().then(() => {
          setTimeout(() => { if (audioElRef.current) audioElRef.current.pause(); }, 200);
        }).catch(() => {});
      }
    }
    setDragging(null);
    setDragOrigin(null);
  }, [dragging, trimStart, trimEnd]);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
      return () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
      };
    }
  }, [dragging, handlePointerMove, handlePointerUp]);

  const handleWaveformClick = useCallback((e) => {
    if (dragging) return;
    if (justDraggedRef.current) { justDraggedRef.current = false; return; }
    const time = getTimeFromX(e.clientX);
    setPlaybackTime(time);
    if (isPlaying) {
      const limit = playMode === 'selection' ? trimEnd : duration;
      playAudio(time, limit);
    }
  }, [dragging, getTimeFromX, isPlaying, playMode, trimEnd, duration, playAudio]);

  const handleTrimAndUpload = async () => {
    setExporting(true);
    stopPlayback();

    const isFullSelection = trimStart < 0.5 && (duration - trimEnd) < 0.5;
    if (isFullSelection) {
      // No trim needed — upload original
      onTrimComplete(file);
      return;
    }

    const MAX_DECODE_SIZE = 150 * 1024 * 1024; // 150MB threshold for in-browser decode
    if (file.size > MAX_DECODE_SIZE) {
      // File too large for browser decode — upload original as-is
      onTrimComplete(file);
      return;
    }

    try {
      const EXPORT_RATE = 16000; // Whisper-native sample rate
      const selectedDuration = trimEnd - trimStart;
      const numFrames = Math.ceil(selectedDuration * EXPORT_RATE);
      const arrayBuffer = await file.arrayBuffer();
      const OfflineCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
      const offCtx = new OfflineCtx(1, numFrames, EXPORT_RATE);
      const decoded = await offCtx.decodeAudioData(arrayBuffer);
      const source = offCtx.createBufferSource();
      source.buffer = decoded;
      source.connect(offCtx.destination);
      source.start(0, trimStart, selectedDuration);
      const rendered = await offCtx.startRendering();
      const wavBlob = encodeWAV(rendered, 0, rendered.length);
      const trimmedFile = new File([wavBlob], file.name.replace(/\.[^.]+$/, '_trimmed.wav'), { type: 'audio/wav' });
      onTrimComplete(trimmedFile);
    } catch (e) {
      console.error('Trim export failed:', e);
      // Fallback: upload original file
      const ok = window.confirm(
        'Browser ran out of memory trimming this large file.\n\n' +
        'Click OK to upload the original file instead, or Cancel to go back and adjust.'
      );
      if (ok) {
        onTrimComplete(file);
      } else {
        setExporting(false);
      }
    }
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 10);
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}.${ms}`;
    return `${m}:${s.toString().padStart(2, '0')}.${ms}`;
  };

  const formatTimeShort = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Parse user-entered time string back to seconds
  const parseTime = (str) => {
    const cleaned = str.trim();
    if (!cleaned) return null;
    const parts = cleaned.split(':');
    let seconds = 0;
    if (parts.length === 3) {
      seconds = (parseInt(parts[0], 10) || 0) * 3600 + (parseInt(parts[1], 10) || 0) * 60 + (parseFloat(parts[2]) || 0);
    } else if (parts.length === 2) {
      seconds = (parseInt(parts[0], 10) || 0) * 60 + (parseFloat(parts[1]) || 0);
    } else {
      seconds = parseFloat(parts[0]) || 0;
    }
    return isNaN(seconds) ? null : Math.max(0, seconds);
  };

  const [editingStart, setEditingStart] = useState(false);
  const [editingEnd, setEditingEnd] = useState(false);
  const [editStartVal, setEditStartVal] = useState('');
  const [editEndVal, setEditEndVal] = useState('');

  const commitStartTime = () => {
    const t = parseTime(editStartVal);
    if (t != null && t < trimEnd - 0.5 && t >= 0) {
      setTrimStart(Math.min(t, duration));
    }
    setEditingStart(false);
  };

  const commitEndTime = () => {
    const t = parseTime(editEndVal);
    if (t != null && t > trimStart + 0.5 && t <= duration) {
      setTrimEnd(t);
    }
    setEditingEnd(false);
  };

  const trimmedDuration = trimEnd - trimStart;
  const savingsPercent = duration > 0 ? Math.round(((duration - trimmedDuration) / duration) * 100) : 0;

  return (
    <div className="trim-modal-overlay">
      <div className="trim-modal" ref={modalRef} role="dialog" aria-modal="true" aria-label="Trim audio" tabIndex={-1} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="trim-modal-header">
          <div className="trim-header-left">
            <div className="trim-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
                <circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/>
                <line x1="20" y1="4" x2="8.12" y2="15.88"/><line x1="14.47" y1="14.48" x2="20" y2="20"/>
                <line x1="8.12" y1="8.12" x2="12" y2="12"/>
              </svg>
            </div>
            <div>
              <h2>Trim Audio</h2>
              <p className="trim-filename">{file.name}</p>
            </div>
          </div>
          <button className="trim-close-btn" onClick={() => { stopPlayback(); onCancel(); }} title="Close (Esc)" aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {loadError ? (
          <div className="trim-loading">
            <p className="status-error-msg">{loadError}</p>
            <button className="btn-secondary" onClick={() => { stopPlayback(); onCancel(); }}>Close</button>
          </div>
        ) : loading ? (
          <div className="trim-loading">
            <div className="trim-load-ring">
              <svg viewBox="0 0 48 48">
                <circle className="trim-load-track" cx="24" cy="24" r="20" />
                <circle className="trim-load-fill" cx="24" cy="24" r="20"
                  style={{ strokeDashoffset: 126 - (126 * loadProgress / 100) }} />
              </svg>
              <span className="trim-load-percent">{loadProgress}%</span>
            </div>
            <p>Decoding audio file...</p>
          </div>
        ) : (
          <>
            {/* Time bar */}
            <div className="trim-time-bar">
              <div className="trim-time-card trim-time-start">
                <span className="trim-time-icon">◀</span>
                <div>
                  <span className="trim-time-label">Start</span>
                  {editingStart ? (
                    <input
                      className="trim-time-input"
                      value={editStartVal}
                      onChange={(e) => setEditStartVal(e.target.value)}
                      onBlur={commitStartTime}
                      onKeyDown={(e) => { if (e.key === 'Enter') commitStartTime(); if (e.key === 'Escape') setEditingStart(false); }}
                      autoFocus
                      placeholder="m:ss.s"
                    />
                  ) : (
                    <span
                      className="trim-time-value editable"
                      onClick={() => { setEditStartVal(formatTime(trimStart)); setEditingStart(true); }}
                      title="Click to type a time"
                    >{formatTime(trimStart)}</span>
                  )}
                </div>
              </div>
              <div className="trim-time-card trim-time-selection">
                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                  <circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/>
                  <line x1="20" y1="4" x2="8.12" y2="15.88" stroke="currentColor" strokeWidth="2" fill="none"/>
                </svg>
                <div>
                  <span className="trim-time-label">Selection</span>
                  <span className="trim-time-value">{formatTime(trimmedDuration)}</span>
                </div>
              </div>
              <div className="trim-time-card trim-time-end">
                <div>
                  <span className="trim-time-label">End</span>
                  {editingEnd ? (
                    <input
                      className="trim-time-input"
                      value={editEndVal}
                      onChange={(e) => setEditEndVal(e.target.value)}
                      onBlur={commitEndTime}
                      onKeyDown={(e) => { if (e.key === 'Enter') commitEndTime(); if (e.key === 'Escape') setEditingEnd(false); }}
                      autoFocus
                      placeholder="m:ss.s"
                    />
                  ) : (
                    <span
                      className="trim-time-value editable"
                      onClick={() => { setEditEndVal(formatTime(trimEnd)); setEditingEnd(true); }}
                      title="Click to type a time"
                    >{formatTime(trimEnd)}</span>
                  )}
                </div>
                <span className="trim-time-icon">▶</span>
              </div>
            </div>

            {/* Waveform */}
            <div className="trim-waveform-wrap">
              <div className="trim-waveform-container" ref={containerRef} onClick={handleWaveformClick}>
                <div className="trim-canvas-wrap">
                  <canvas ref={canvasRef} className="trim-waveform-canvas" />
                </div>

                {/* Handles */}
                <div
                  className={`trim-handle trim-handle-start ${dragging === 'start' ? 'dragging' : ''}`}
                  style={{ left: `${(trimStart / duration) * 100}%` }}
                  onPointerDown={(e) => handlePointerDown(e, 'start')}
                >
                  <div className="trim-handle-grip">
                    <span /><span /><span />
                  </div>
                </div>

                <div
                  className="trim-region"
                  style={{
                    left: `${(trimStart / duration) * 100}%`,
                    width: `${((trimEnd - trimStart) / duration) * 100}%`
                  }}
                  onPointerDown={(e) => handlePointerDown(e, 'region')}
                />

                <div
                  className={`trim-handle trim-handle-end ${dragging === 'end' ? 'dragging' : ''}`}
                  style={{ left: `${(trimEnd / duration) * 100}%` }}
                  onPointerDown={(e) => handlePointerDown(e, 'end')}
                >
                  <div className="trim-handle-grip">
                    <span /><span /><span />
                  </div>
                </div>
              </div>

              {/* Time axis */}
              <div className="trim-time-axis">
                {[0, 0.25, 0.5, 0.75, 1].map((pct) => (
                  <span key={pct} style={{ left: `${pct * 100}%` }}>{formatTimeShort(pct * duration)}</span>
                ))}
              </div>
            </div>

            {/* Transport controls */}
            <div className="trim-transport">
              <div className="trim-transport-left">
                <button
                  className={`trim-transport-btn trim-btn-play ${isPlaying ? 'active' : ''}`}
                  onClick={() => {
                    if (isPlaying) { stopPlayback(); }
                    else { setPlayMode('selection'); playAudio(trimStart, trimEnd); }
                  }}
                  title="Play selection (Space)"
                  aria-label={isPlaying && playMode === 'selection' ? 'Pause' : 'Play selection'}
                >
                  {isPlaying && playMode === 'selection' ? (
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M8,5.14V19.14L19,12.14L8,5.14Z"/></svg>
                  )}
                </button>
                <button
                  className={`trim-transport-btn trim-btn-full ${isPlaying && playMode === 'full' ? 'active' : ''}`}
                  onClick={() => {
                    if (isPlaying && playMode === 'full') { stopPlayback(); }
                    else { setPlayMode('full'); playAudio(0, duration); }
                  }}
                  title="Play full audio"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <polygon points="5 3 19 12 5 21 5 3"/>
                  </svg>
                  Full
                </button>
              </div>

              <div className="trim-transport-info">
                <span className="trim-savings-badge">
                  {savingsPercent > 0 ? `−${savingsPercent}%` : 'Full length'}
                </span>
                <span className="trim-transport-duration">
                  {formatTimeShort(trimmedDuration)} <span className="trim-of">of</span> {formatTimeShort(duration)}
                </span>
              </div>
            </div>

            {/* Keyboard hint */}
            <div className="trim-keyboard-hint">
              <kbd>Space</kbd> Play/Pause &nbsp; <kbd>Esc</kbd> Cancel &nbsp; Drag handles to set trim points
            </div>

            {/* Actions */}
            <div className="trim-actions">
              <button className="trim-cancel-btn" onClick={() => { stopPlayback(); onCancel(); }} disabled={exporting}>
                Cancel
              </button>
              <button className="trim-skip-btn" onClick={() => { stopPlayback(); onTrimComplete(file); }} disabled={exporting}
                title="Upload the original file without trimming">
                Upload Original
              </button>
              <button className="trim-upload-btn" onClick={handleTrimAndUpload} disabled={exporting}>
                {exporting ? (
                  <>
                    <span className="trim-export-spinner" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                      <circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/>
                      <line x1="20" y1="4" x2="8.12" y2="15.88"/><line x1="14.47" y1="14.48" x2="20" y2="20"/>
                      <line x1="8.12" y1="8.12" x2="12" y2="12"/>
                    </svg>
                    Trim & Upload
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================
// Service capacity — shared between transcription and translation upload
// panels so both reflect the same pause state (manual or automatic, e.g.
// daily GPU budget exhausted) without polling/duplicating the check twice.
// ============================================
function useServiceCapacity(profile) {
  const [state, setState] = useState({ transcriptionAvailable: true, translationAvailable: true });
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(`${API_BASE}/capacity`);
        const data = await res.json();
        if (cancelled) return;
        setState({
          transcriptionAvailable: data.transcriptionAvailable !== false,
          translationAvailable: data.translationAvailable !== false,
        });
      } catch (_) {
        // A failed check shouldn't itself block uploads — only an explicit
        // *Available:false response does that.
      }
    };
    check();
    const timer = setInterval(check, 30000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);
  return profile === 'translation' ? state.translationAvailable : state.transcriptionAvailable;
}

function ServicePausedBanner({ profile }) {
  const label = profile === 'translation' ? 'Translation' : 'Transcription';
  return (
    <div className="service-paused-banner" role="status">
      <span className="service-paused-icon" aria-hidden="true">⏸</span>
      <div>
        <strong>{label} is temporarily paused</strong>
        <p>{label} is on hold right now — uploads are disabled until it's back. Please check back soon.</p>
      </div>
    </div>
  );
}

// ============================================
// Upload Panel Component
// ============================================
function UploadPanel({ onUploadComplete, isUploading, setIsUploading }) {
  const { getToken } = useAuth();
  const capacityAvailable = useServiceCapacity('transcription');
  const [dragActive, setDragActive] = useState(false);
  const [progress, setProgress] = useState(0);
  const [interpreterMode, setInterpreterMode] = useState(false);
  const [englishOnly, setEnglishOnly] = useState(true);
  const [trimEnabled, setTrimEnabled] = useState(false);
  const [trimFile, setTrimFile] = useState(null);
  const [converting, setConverting] = useState(false);
  const [convertProgress, setConvertProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const uploadFile = async (file) => {
    setIsUploading(true);
    setProgress(0);
    setUploadError(null);

    try {
      const langMode = interpreterMode ? (englishOnly ? 'en_only' : 'both') : 'standard';
      const token = await getToken();

      // Step 1: get pre-signed S3 URL
      const urlRes = await fetch(`${API_BASE}/get-upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ filename: file.name, contentType: file.type || 'audio/mpeg', langMode }),
      });
      if (!urlRes.ok) {
        const err = await urlRes.json().catch(() => ({}));
        throw new Error(err.detail || `Failed to get upload URL: ${urlRes.status}`);
      }
      const { uploadUrl, jobId, blobName } = await urlRes.json();

      // Step 2: upload file directly to S3 with progress tracking
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        });
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'audio/mpeg');
        xhr.onload = () => {
          if (xhr.status < 300) resolve();
          else reject(new Error(`S3 upload failed: ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('Network error — could not reach server'));
        xhr.send(file);
      });

      // Get a fresh token — large uploads can outlive the original 60s JWT
      const freshToken = await getToken();
      const completeRes = await fetch(`${API_BASE}/upload-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${freshToken}` },
        body: JSON.stringify({ jobId, blobName, interpreterMode, englishOnly, originalName: file.name }),
      });
      if (!completeRes.ok) {
        const err = await completeRes.json().catch(() => ({}));
        throw new Error(err.detail || `Failed to queue job: ${completeRes.status}`);
      }

      onUploadComplete(jobId, file.name);
      setIsUploading(false);
      setProgress(0);
    } catch (error) {
      setUploadError(toUserMessage(error, 'upload'));
      setIsUploading(false);
      setProgress(0);
    }
  };

  const handleFile = async (f) => {
    if (!capacityAvailable) return;
    const type = await detectFileType(f);

    if (type === 'video') {
      setConverting(true);
      setConvertProgress(0);
      try {
        const audioFile = await convertVideoToAudio(f, setConvertProgress);
        setConverting(false);
        if (trimEnabled) {
          setTrimFile(audioFile);
        } else {
          uploadFile(audioFile);
        }
      } catch (err) {
        console.error('Video conversion failed, uploading original:', err);
        setConverting(false);
        if (trimEnabled) {
          setTrimFile(f);
        } else {
          uploadFile(f);
        }
      }
    } else {
      if (trimEnabled) {
        setTrimFile(f);
      } else {
        uploadFile(f);
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  return (
    <div className="upload-panel">
      <h2>Upload Audio or Video</h2>

      {!capacityAvailable && <ServicePausedBanner profile="transcription" />}

      <div
        className={`drop-zone ${dragActive ? 'active' : ''} ${(isUploading || converting) ? 'uploading' : ''} ${!capacityAvailable ? 'disabled' : ''}`}
        onDragEnter={capacityAvailable ? handleDrag : undefined}
        onDragLeave={capacityAvailable ? handleDrag : undefined}
        onDragOver={capacityAvailable ? handleDrag : undefined}
        onDrop={capacityAvailable ? handleDrop : undefined}
        onClick={() => capacityAvailable && !isUploading && !converting && fileInputRef.current?.click()}
        aria-disabled={!capacityAvailable}
      >
        {converting ? (
          <div className="upload-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${convertProgress}%` }} />
            </div>
            <span>Extracting audio... {convertProgress}%</span>
          </div>
        ) : isUploading ? (
          <div className="upload-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span>{progress}% uploaded</span>
          </div>
        ) : (
          <>
            <div className="drop-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            <p>Drag & drop audio or video file or click to browse</p>
            <span className="file-types">MP3, WAV, M4A, FLAC · MP4, MOV, MKV, AVI</span>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*,video/*"
          onChange={handleFileSelect}
          disabled={!capacityAvailable}
          hidden
        />
      </div>

      {uploadError && <p className="pdf-error">{uploadError}</p>}

      <div className="upload-options">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={trimEnabled}
            onChange={(e) => setTrimEnabled(e.target.checked)}
            disabled={isUploading}
          />
          Trim audio before upload
        </label>

        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={interpreterMode}
            onChange={(e) => setInterpreterMode(e.target.checked)}
            disabled={isUploading}
          />
          Interpreter present
        </label>

        {interpreterMode && (
          <div className="interpreter-options">
            <label className="radio-label">
              <input
                type="radio"
                name="transcribeOption"
                checked={englishOnly}
                onChange={() => setEnglishOnly(true)}
                disabled={isUploading}
              />
              English only (skip interpreter)
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="transcribeOption"
                checked={!englishOnly}
                onChange={() => setEnglishOnly(false)}
                disabled={isUploading}
              />
              Both languages (separate files)
            </label>
          </div>
        )}

      </div>

      {trimFile && (
        <TrimModal
          file={trimFile}
          onTrimComplete={(trimmedFile) => {
            setTrimFile(null);
            uploadFile(trimmedFile);
          }}
          onCancel={() => setTrimFile(null)}
        />
      )}
    </div>
  );
}

// ============================================
// Jobs History Table Component
// ============================================
function JobsHistory({ jobs, onSelectJob, onDeleteJob, currentJobId }) {
  const formatDuration = (seconds) => {
    if (seconds == null || seconds === false) return '-';
    const num = Number(seconds);
    if (isNaN(num) || num < 0) return '-';
    const mins = Math.floor(num / 60);
    const secs = Math.floor(num % 60);
    return `${mins}m ${secs}s`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'completed': { class: 'status-completed', label: 'Completed' },
      'processing': { class: 'status-processing', label: 'Processing' },
      'queued': { class: 'status-queued', label: 'Queued' },
      'waiting_for_capacity': { class: 'status-queued', label: 'Waiting for GPU' },
      'provisioning': { class: 'status-processing', label: 'Starting GPU' },
      'failed': { class: 'status-failed', label: 'Failed' }
    };
    const s = statusMap[status] || { class: 'status-unknown', label: status };
    return <span className={`status-badge ${s.class}`}>{s.label}</span>;
  };

  if (!jobs || jobs.length === 0) {
    return (
      <div className="jobs-history empty">
        <p>No recordings yet. Upload an audio or video file to get started.</p>
      </div>
    );
  }

  return (
    <div className="jobs-history">
      <h3>Recent Transcriptions</h3>
      <div className="jobs-table-wrapper">
        <table className="jobs-table">
          <thead>
            <tr>
              <th>File Name</th>
              <th>Status</th>
              <th>Audio Duration</th>
              <th>Processing Time</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job) => (
              <tr key={job.jobId} className={currentJobId === job.jobId ? 'active' : ''}>
                <td className="job-name" title={job.originalName}>
                  {job.originalName || job.jobId}
                </td>
                <td>{getStatusBadge(job.status)}</td>
                <td>{formatDuration(job.audioDuration)}</td>
                <td>{formatDuration(job.processingTime)}</td>
                <td>{formatDate(job.createdAt)}</td>
                <td className="job-actions">
                  {job.status === 'completed' && (
                    <button className="btn-view" onClick={() => onSelectJob(job)}>
                      View
                    </button>
                  )}
                  <button className="btn-delete" onClick={() => onDeleteJob(job.jobId)} title="Delete recording" aria-label="Delete recording">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================
// Status Panel Component (Animated Processing Page)
// ============================================
const PROCESSING_STEPS = [
  { key: 'uploaded', label: 'Recording received', icon: '📤' },
  { key: 'queued', label: 'Getting ready', icon: '📋' },
  { key: 'processing', label: 'Writing out what was said', icon: '🧠' },
  { key: 'completed', label: 'Transcript ready', icon: '✅' },
];

const TRANSLATION_STEPS = [
  { key: 'uploaded', label: 'Document received', icon: '📤' },
  { key: 'queued', label: 'Getting ready', icon: '📋' },
  { key: 'processing', label: 'Translating', icon: '🌍' },
  { key: 'completed', label: 'Translation ready', icon: '✅' },
];

function getStepIndex(status) {
  if (status === 'completed') return 3;
  if (status === 'processing') return 2;
  if (status === 'queued' || status === 'waiting_for_capacity' || status === 'provisioning') return 1;
  return 0;
}

function StatusPanel({ jobId, fileName, onTranscriptReady, onStatusUpdate, mode = 'transcription', targetLanguages, onStartOver }) {
  const [statusDetails, setStatusDetails] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [dots, setDots] = useState('');
  const [pollErrors, setPollErrors] = useState(0);
  const pollInterval = useRef(null);
  const timerInterval = useRef(null);
  const dotsInterval = useRef(null);
  const startTime = useRef(Date.now());

  // Keep callbacks in refs so changing them never restarts the polling timer
  const onTranscriptReadyRef = useRef(onTranscriptReady);
  const onStatusUpdateRef = useRef(onStatusUpdate);
  const modeRef = useRef(mode);
  const targetLanguagesRef = useRef(targetLanguages);
  useEffect(() => { onTranscriptReadyRef.current = onTranscriptReady; }, [onTranscriptReady]);
  useEffect(() => { onStatusUpdateRef.current = onStatusUpdate; }, [onStatusUpdate]);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { targetLanguagesRef.current = targetLanguages; }, [targetLanguages]);

  useEffect(() => {
    if (!jobId) return;
    startTime.current = Date.now();
    setElapsedTime(0);

    timerInterval.current = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime.current) / 1000));
    }, 1000);

    dotsInterval.current = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);

    const checkStatus = async () => {
      try {
        const response = await fetch(`${API_BASE}/status/${jobId}`);
        if (!response.ok) {
          setPollErrors(prev => prev + 1);
          return;
        }
        const data = await response.json();
        setPollErrors(0);
        setStatusDetails(data);
        onStatusUpdateRef.current?.(data);

        if (data.status === 'completed') {
          const currentMode = modeRef.current;
          const currentLangs = targetLanguagesRef.current;
          // For translation mode, wait until at least one target language translation is done
          const translationsDone = currentMode === 'translation' && currentLangs?.length
            ? currentLangs.some(l => data.translations?.[l] === 'completed')
            : true;
          const translationsFailed = currentMode === 'translation' && currentLangs?.length
            ? currentLangs.every(l => data.translations?.[l] === 'failed')
            : false;
          if (translationsFailed) {
            clearInterval(pollInterval.current);
            clearInterval(timerInterval.current);
            clearInterval(dotsInterval.current);
            return;
          }
          if (!translationsDone) return; // keep polling — extraction done but translation still running
          clearInterval(pollInterval.current);
          clearInterval(timerInterval.current);
          clearInterval(dotsInterval.current);
          onTranscriptReadyRef.current(data);
        } else if (data.status === 'failed') {
          clearInterval(pollInterval.current);
          clearInterval(timerInterval.current);
          clearInterval(dotsInterval.current);
        }
      } catch (error) {
        console.error('Status check error:', error);
        setPollErrors(prev => prev + 1);
      }
    };

    checkStatus();
    pollInterval.current = setInterval(checkStatus, 5000);

    return () => {
      clearInterval(pollInterval.current);
      clearInterval(timerInterval.current);
      clearInterval(dotsInterval.current);
    };
  }, [jobId]); // only jobId — callbacks captured in refs to avoid resetting the timer

  if (!jobId) return null;

  const isTranslation = mode === 'translation';
  const currentStatus = statusDetails?.status || 'queued';

  // If extraction is done but translation files haven't landed yet, keep showing "processing"
  const translationPending = mode === 'translation' && currentStatus === 'completed'
    && targetLanguages?.length
    && !targetLanguages.some(l => statusDetails?.translations?.[l] === 'completed');
  const translationFailed = mode === 'translation' && targetLanguages?.length
    && targetLanguages.every(l => statusDetails?.translations?.[l] === 'failed');
  const displayStatus = translationFailed ? 'failed' : translationPending ? 'processing' : currentStatus;

  const activeStep = getStepIndex(displayStatus);
  const isActive = ['processing', 'queued', 'waiting_for_capacity', 'provisioning'].includes(displayStatus);

  const formatElapsed = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const langCount = targetLanguages?.length || 1;
  const estimatedMin = isTranslation ? 3 * langCount : 8;
  const estimatedMax = isTranslation ? 8 * langCount : 12;
  const activeTranslationProgress = isTranslation
    ? targetLanguages?.map(lang => statusDetails?.translationProgress?.[lang]).find(Boolean)
    : null;
  const translationStage = activeTranslationProgress?.stage;
  const translationSectionPercent = Number(activeTranslationProgress?.percent || 0);
  // A job sitting in the queue looked identical to one actively translating —
  // same title, same rotating tip, no indication anything was waiting on a
  // free GPU slot. Give "queued" its own distinct, honest state instead.
  const transcriptionStage = statusDetails?.progress?.stage;
  const isCapacityWait = displayStatus === 'waiting_for_capacity';
  const isProvisioning = displayStatus === 'provisioning';
  const isQueued = isTranslation
    ? translationStage === 'queued'
    : ['queued', 'waiting_for_capacity', 'provisioning'].includes(displayStatus);
  const steps = (isTranslation ? TRANSLATION_STEPS : PROCESSING_STEPS).map((step, idx) =>
    isQueued && idx === 1
      ? { ...step, label: isCapacityWait ? 'Waiting for GPU capacity' : isProvisioning ? 'Starting GPU worker' : 'Waiting in queue' }
      : step
  );
  const progressPercent = displayStatus === 'completed' ? 100
    : !isActive ? 0
    : isTranslation && activeTranslationProgress
      ? translationStage === 'completed' ? 100
        : translationStage === 'rebuilding_document' ? 90
        : translationStage === 'translating' ? Math.min(88, 10 + Math.round(translationSectionPercent * 0.78))
        : translationStage === 'preparing_document' ? 8
        : translationStage === 'failed' ? 0
        : translationStage === 'queued' ? 0
        : 5
      : isTranslation ? 5
      : isQueued ? 0
      : ({
          downloading: 10,
          preparing_audio: 20,
          transcribing: 45,
          post_processing: 85,
          saving: 95,
          completed: 100,
        }[transcriptionStage] ?? 5);

  const titleActive = isCapacityWait
    ? 'Waiting for GPU capacity'
    : isProvisioning
      ? 'Starting transcription worker'
      : isQueued
        ? 'Waiting in queue'
        : isTranslation ? `Translating${dots}` : `Transcribing${dots}`;
  const titleComplete = isTranslation ? 'Translation Complete!' : 'Transcription Complete!';
  const queuePosition = activeTranslationProgress?.queuePosition;
  const queueDepth = activeTranslationProgress?.queueDepth;
  const activeWorkers = activeTranslationProgress?.activeWorkers || 0;
  const transcriptionQueuePosition = statusDetails?.queuePosition;
  const transcriptionQueueDepth = statusDetails?.queueDepth;
  const capacityMessage = statusDetails?.capacity?.message;
  const queueStatusText = !isTranslation && isQueued
    ? isCapacityWait
      ? `${capacityMessage || 'GPU processing capacity is temporarily unavailable.'} Your recording is safely queued${transcriptionQueuePosition ? ` at position ${transcriptionQueuePosition} of ${transcriptionQueueDepth}` : ''}.`
      : isProvisioning
        ? 'A GPU transcription worker is starting. Your recording will begin automatically.'
        : `Your recording is safely queued${transcriptionQueuePosition ? ` at position ${transcriptionQueuePosition} of ${transcriptionQueueDepth}` : ''}.`
    : isQueued && queuePosition
      ? (queuePosition <= activeWorkers
        ? 'Next in line — a translator will pick this up any moment.'
        : `Waiting in queue (position ${queuePosition} of ${queueDepth}) — ${activeWorkers} translator${activeWorkers === 1 ? '' : 's'} currently working.`)
    : isQueued
      ? 'Waiting for a translator to become available…'
      : null;
  const liveStatusText = queueStatusText || (isTranslation && activeTranslationProgress?.message
    ? activeTranslationProgress.message
    : null);
  const estimateText = isTranslation
    ? 'Translation time depends on document length — longer documents take longer.'
    : `Estimated: ${estimatedMin}–${estimatedMax} minutes for a 2-hour sermon`;
  const errorMsg = isTranslation
    ? 'Translation failed — please try uploading the document again.'
    : 'Transcription failed — please try uploading the file again.';

  return (
    <div className="status-panel-v2">
      {/* Pulsing ring animation */}
      <div className="status-ring-container">
        <svg className="status-ring" viewBox="0 0 120 120">
          <circle className="ring-bg" cx="60" cy="60" r="52" />
          <circle
            className={`ring-progress ${isActive ? 'ring-animated' : ''} ${isQueued ? 'ring-queued' : ''}`}
            cx="60" cy="60" r="52"
            style={{ strokeDashoffset: 327 - (327 * progressPercent / 100) }}
          />
        </svg>
        <div className="status-ring-inner">
          <span className="status-ring-percent">{progressPercent}%</span>
          <span className="status-ring-elapsed">{formatElapsed(elapsedTime)}</span>
        </div>
      </div>

      {/* Title & subtitle */}
      <h3 className="status-v2-title">
        {displayStatus === 'completed' ? titleComplete :
         displayStatus === 'failed' ? 'Processing Failed' :
         titleActive}
      </h3>
      {fileName && <p className="status-v2-filename">{fileName}</p>}

      {/* Step tracker */}
      <div className="status-steps">
        {steps.map((step, idx) => {
          const isDone = idx < activeStep;
          const isCurrent = idx === activeStep;
          return (
            <div key={step.key} className={`status-step ${isDone ? 'done' : ''} ${isCurrent ? 'current' : ''}`}>
              <div className="step-icon-row">
                <div className={`step-dot ${isDone ? 'done' : ''} ${isCurrent ? 'current' : ''}`}>
                  {isDone ? '✓' : step.icon}
                </div>
                {idx < steps.length - 1 && (
                  <div className={`step-line ${isDone ? 'done' : ''}`} />
                )}
              </div>
              <span className="step-label">{step.label}</span>
            </div>
          );
        })}
      </div>

      {/* Live info bar */}
      {isActive && (
        <div className={`status-live-bar ${isQueued ? 'queued' : ''}`}>
          <div className="live-dot" />
          <span>{liveStatusText || estimateText}</span>
        </div>
      )}

      {/* Tips */}
      {isActive && !isQueued && (isTranslation ? <TranslationTips /> : <StatusTips />)}

      {displayStatus === 'failed' && (
        <div className="status-error-block">
          <p className="status-error-msg">{errorMsg}</p>
          {statusDetails?.error && toUserMessage(statusDetails.error, mode) !== errorMsg && (
            <p className="status-error-detail">{toUserMessage(statusDetails.error, mode)}</p>
          )}
          {onStartOver && (
            <button className="btn-secondary" onClick={onStartOver}>Start Over</button>
          )}
        </div>
      )}

      {pollErrors >= 3 && (
        <p className="status-error-msg">Having trouble checking status. Still trying...</p>
      )}
    </div>
  );
}

function StatusTips() {
  const tips = [
    '🎵 Listening closely to your recording...',
    '🔤 Identifying speakers and segmenting dialogue...',
    '⚡ Generating timestamps for each segment...',
    '📝 Building paragraph structure from speech patterns...',
    '🌍 Detecting language and optimizing accuracy...',
    '🎯 Fine-tuning punctuation and formatting...',
  ];
  const [tipIdx, setTipIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTipIdx(prev => (prev + 1) % tips.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="status-tips">
      <p className="status-tip-text" key={tipIdx}>{tips[tipIdx]}</p>
    </div>
  );
}

function TranslationTips() {
  const tips = [
    '🌍 Extracting text and paragraph structure from your document...',
    '📖 Applying glossary terms to ensure consistent terminology...',
    '🔤 Translating paragraph by paragraph for accuracy...',
    '✏️ Preserving formatting and document structure...',
    '🔍 Checking translation consistency across sections...',
    '📄 Preparing your translated document for download...',
  ];
  const [tipIdx, setTipIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTipIdx(prev => (prev + 1) % tips.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="status-tips">
      <p className="status-tip-text" key={tipIdx}>{tips[tipIdx]}</p>
    </div>
  );
}

// ============================================
// Audio Player Component
// ============================================
const AudioPlayer = React.forwardRef(({ audioUrl, currentTime, onTimeUpdate, onSeek, isPlaying, setIsPlaying, onDurationLoaded }, ref) => {
  const audioRef = useRef(null);
  const [duration, setDuration] = useState(0);

  React.useImperativeHandle(ref, () => ({
    seekAndPlay: (time) => {
      if (audioRef.current) {
        audioRef.current.currentTime = time;
        audioRef.current.play();
        setIsPlaying(true);
        onSeek(time);
      }
    },
    pause: () => {
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    }
  }));

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      onTimeUpdate(audioRef.current.currentTime);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      onSeek(newTime);
    }
  };

  const skipTime = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
    }
  };

  return (
    <div className="audio-player">
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={() => {
          const d = audioRef.current?.duration || 0;
          setDuration(d);
          if (d > 0 && onDurationLoaded) onDurationLoaded(d);
        }}
        onEnded={() => setIsPlaying(false)}
      />
      
      <div className="player-controls">
        <button className="player-btn skip-btn" onClick={() => skipTime(-10)} aria-label="Skip back 10 seconds">
          <span>-10s</span>
        </button>

        <button className="player-btn play-btn" onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}>
          {isPlaying ? (
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M14,19H18V5H14M6,19H10V5H6V19Z"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M8,5.14V19.14L19,12.14L8,5.14Z"/>
            </svg>
          )}
        </button>

        <button className="player-btn skip-btn" onClick={() => skipTime(10)} aria-label="Skip forward 10 seconds">
          <span>+10s</span>
        </button>
      </div>

      {isPlaying && (
        <div className="now-playing">
          🔊 Now playing at {formatTime(currentTime)}
        </div>
      )}

      <div className="player-timeline" onClick={handleSeek}>
        <div className="timeline-progress" style={{ width: `${(currentTime / duration) * 100}%` }} />
        <div className="timeline-handle" style={{ left: `${(currentTime / duration) * 100}%` }} />
      </div>

      <div className="player-time">
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>
    </div>
  );
});

// ============================================
// Transcript Segment Component
// ============================================
function TranscriptSegment({ segment, isActive, isPlaying, onClick, onDoubleClick, isEditing, editText, setEditText, onSave, onCancel, showTimestamps, saveToGlossary, onToggleGlossary }) {
  if (isEditing) {
    return (
      <div className="segment-edit-container">
        <div className="segment-edit-header">
          <span className="segment-edit-timestamp">{segment.ts}</span>
          <span className="segment-edit-label">Editing segment</span>
        </div>
        <textarea
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          autoFocus
          rows={3}
        />
        <div className="segment-edit-actions">
          <label className="seg-glossary-check">
            <input type="checkbox" checked={saveToGlossary} onChange={e => onToggleGlossary(e.target.checked)} />
            Save to glossary
          </label>
          <button className="btn-cancel" onClick={onCancel}>Cancel</button>
          <button className="btn-save" onClick={onSave}>✓ Save</button>
        </div>
      </div>
    );
  }

  return (
    <span 
      className={`segment ${isActive ? 'active' : ''}`}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      title="Click to play • Double-click to edit"
    >
      {showTimestamps && (
        <span className="timestamp">
          {isActive && isPlaying && '▶ '}
          {segment.ts}
        </span>
      )}
      <span className="text">{segment.text}</span>
      {' '}
    </span>
  );
}

// ============================================
// Transcript Editor Component
// ============================================
function TranscriptEditor({ transcript, audioUrl, onSave, onAudioDurationLoaded }) {
  const { getToken } = useAuth();
  const [paragraphs, setParagraphs] = useState([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [editingSegment, setEditingSegment] = useState(null);
  const [editText, setEditText] = useState('');
  const [originalSegText, setOriginalSegText] = useState('');
  const [saveToGlossary, setSaveToGlossary] = useState(true);
  const [glossaryMsg, setGlossaryMsg] = useState(null);
  const [saveMsg, setSaveMsg] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTimestamps, setShowTimestamps] = useState(true);
  const transcriptRef = useRef(null);
  const audioPlayerRef = useRef(null);

  useEffect(() => {
    if (transcript?.paragraphs) {
      setParagraphs(transcript.paragraphs);
    }
  }, [transcript]);

  const findActiveSegment = useCallback(() => {
    for (let pIdx = 0; pIdx < paragraphs.length; pIdx++) {
      const para = paragraphs[pIdx];
      for (let sIdx = 0; sIdx < para.segments.length; sIdx++) {
        const seg = para.segments[sIdx];
        if (currentTime >= seg.start && currentTime <= seg.end) {
          return { pIdx, sIdx };
        }
      }
    }
    return null;
  }, [currentTime, paragraphs]);

  const activeSegment = findActiveSegment();

  useEffect(() => {
    if (activeSegment && transcriptRef.current && isPlaying) {
      const activeEl = transcriptRef.current.querySelector('.segment.active');
      if (activeEl) {
        activeEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [activeSegment, isPlaying]);

  const handleSegmentClick = (pIdx, sIdx, segment) => {
    if (editingSegment !== null) return;
    if (audioPlayerRef.current) {
      audioPlayerRef.current.seekAndPlay(segment.start);
    }
  };

  const handleSegmentDoubleClick = (pIdx, sIdx, segment) => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
    }
    setEditingSegment({ pIdx, sIdx });
    setEditText(segment.text);
    setOriginalSegText(segment.text);
  };

  const handleSegmentSave = async () => {
    if (editingSegment === null) return;
    const { pIdx, sIdx } = editingSegment;
    const newParagraphs = [...paragraphs];
    newParagraphs[pIdx].segments[sIdx].text = editText;
    setParagraphs(newParagraphs);
    setEditingSegment(null);
    setHasChanges(true);

    if (saveToGlossary && editText.trim() !== originalSegText.trim()) {
      const pair = diffWords(originalSegText, editText);
      if (pair) {
        try {
          const token = await getToken();
          const r = await fetch(`${API_BASE}/glossary/terms`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ original: pair.original, corrected: pair.corrected, category: 'Names', mode: 'target_correction' }),
          });
          if (r.ok) {
            setGlossaryMsg(`✓ Added "${pair.original} → ${pair.corrected}" to glossary`);
            setTimeout(() => setGlossaryMsg(null), 4000);
          }
        } catch (_) {
          // silent — transcript edit already saved
        }
      }
    }
    setEditText('');
    setOriginalSegText('');
  };

  const handleSegmentCancel = () => {
    setEditingSegment(null);
    setEditText('');
  };

  const handleSaveAll = async () => {
    setSaveError(null);
    try {
      await onSave(paragraphs);
      setHasChanges(false);
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(null), 3000);
    } catch (error) {
      setSaveError(toUserMessage(error, 'save'));
    }
  };

  return (
    <div className="transcript-editor">
      <div className="editor-header">
        <h2>Transcript Editor</h2>
        <div className="editor-hint">
          <span className="hint-badge">Click</span> to play
          <span className="hint-separator">•</span>
          <span className="hint-badge">Double-click</span> to edit
        </div>
        <label className="timestamp-toggle">
          <span className="timestamp-toggle-label">Timestamps</span>
          <button
            className={`toggle-switch ${showTimestamps ? 'on' : ''}`}
            onClick={() => setShowTimestamps(prev => !prev)}
            role="switch"
            aria-checked={showTimestamps}
          >
            <span className="toggle-knob" />
          </button>
        </label>
        {hasChanges && (
          <button className="btn-primary" onClick={handleSaveAll}>
            Save Changes
          </button>
        )}
      </div>
      {glossaryMsg && <div className="editor-glossary-toast">{glossaryMsg}</div>}
      {saveMsg && <div className="editor-glossary-toast">{saveMsg}</div>}
      {saveError && <p className="status-error-msg">{saveError}</p>}

      {audioUrl && (
        <AudioPlayer
          ref={audioPlayerRef}
          audioUrl={audioUrl}
          currentTime={currentTime}
          onTimeUpdate={setCurrentTime}
          onSeek={setCurrentTime}
          isPlaying={isPlaying}
          setIsPlaying={setIsPlaying}
          onDurationLoaded={onAudioDurationLoaded}
        />
      )}

      <div className="transcript-content" ref={transcriptRef}>
        {paragraphs.map((para, pIdx) => (
          <div key={pIdx} className="paragraph">
            {para.segments.map((seg, sIdx) => (
              <TranscriptSegment
                key={`${pIdx}-${sIdx}`}
                segment={seg}
                isActive={activeSegment?.pIdx === pIdx && activeSegment?.sIdx === sIdx}
                isPlaying={isPlaying}
                isEditing={editingSegment?.pIdx === pIdx && editingSegment?.sIdx === sIdx}
                editText={editText}
                setEditText={setEditText}
                onClick={() => handleSegmentClick(pIdx, sIdx, seg)}
                onDoubleClick={() => handleSegmentDoubleClick(pIdx, sIdx, seg)}
                onSave={handleSegmentSave}
                onCancel={handleSegmentCancel}
                showTimestamps={showTimestamps}
                saveToGlossary={saveToGlossary}
                onToggleGlossary={setSaveToGlossary}
              />
            ))}
            {showTimestamps && <span className="paragraph-end-time">{para.end_ts}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// Translation Segment — mirrors TranscriptSegment but without timestamps
// ============================================
// A paragraph that failed to translate gets its raw engine error stuffed into
// para.text by the backend (so the job can still complete instead of stalling
// the whole document) — detect that shape and never show the raw string to
// the user, even though the underlying data is technically "there".
const RAW_TRANSLATION_ERROR = /^\[TRANSLATION_FAILED:/;
function isFailedTranslationText(text) {
  return typeof text === 'string' && RAW_TRANSLATION_ERROR.test(text.trim());
}

function TranslationSegment({ para, isActive, isEditing, editText, setEditText, onClick, onDoubleClick, onSave, onCancel, saveToGlossary, onToggleGlossary, lang }) {
  if (isEditing) {
    return (
      <div className="segment-edit-container">
        <div className="segment-edit-header">
          <span className="segment-edit-label">Editing paragraph</span>
        </div>
        <textarea
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          autoFocus
          rows={Math.max(3, Math.ceil(editText.length / 80))}
        />
        <div className="segment-edit-actions">
          {lang !== 'en' && (
            <label className="seg-glossary-check">
              <input type="checkbox" checked={saveToGlossary} onChange={e => onToggleGlossary(e.target.checked)} />
              Save to glossary
            </label>
          )}
          <button className="btn-cancel" onClick={onCancel}>Cancel</button>
          <button className="btn-save" onClick={onSave}>✓ Save</button>
        </div>
      </div>
    );
  }
  const failed = isFailedTranslationText(para.text);
  return (
    <span
      className={`segment${isActive ? ' active' : ''}${failed ? ' segment-failed' : ''}`}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      title={failed
        ? 'This paragraph could not be translated — double-click to fix it manually'
        : 'Click to locate in document • Double-click to edit'}
    >
      <span className="text">
        {failed ? 'This paragraph could not be translated — double-click to fix it manually.' : para.text}
      </span>
      {' '}
    </span>
  );
}

// ============================================
// Export Options Component
// ============================================
// ============================================
// PDF Translate Panel — upload sermon PDF for translation
// ============================================
function TranslatePdfPanel({ onJobQueued }) {
  const { getToken } = useAuth();
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState(null);
  const [selectedLangs, setSelectedLangs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) { setError('Only PDF files are accepted.'); return; }
    setError(null);
    setFile(f);
  };

  const handleDrop = (e) => { e.preventDefault(); setDragActive(false); handleFile(e.dataTransfer.files[0]); };

  const handleSubmit = async () => {
    if (!file || !selectedLangs.length) return;
    setUploading(true);
    setError(null);
    try {
      const token = await getToken();
      const authHeader = { Authorization: `Bearer ${token}` };

      const urlRes = await fetch(`${API_BASE}/pdf/get-upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ filename: file.name }),
      });
      if (!urlRes.ok) throw new Error('Failed to get upload URL');
      const { uploadUrl, jobId, blobName } = await urlRes.json();

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', 'application/pdf');
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`)));
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(file);
      });

      const qRes = await fetch(`${API_BASE}/pdf/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ jobId, blobName, targetLanguages: selectedLangs, originalName: file.name }),
      });
      if (!qRes.ok) throw new Error('Failed to queue translation');

      onJobQueued(jobId, file.name, selectedLangs);
      setFile(null);
      setSelectedLangs([]);
    } catch (e) {
      setError(toUserMessage(e, 'translation'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="pdf-panel">
      <div
        className={`pdf-dropzone${dragActive ? ' active' : ''}${file ? ' has-file' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        onClick={() => !file && fileInputRef.current?.click()}
      >
        <input ref={fileInputRef} type="file" accept=".pdf" style={{ display: 'none' }}
          onChange={e => handleFile(e.target.files[0])} />
        {file ? (
          <div className="pdf-file-selected">
            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
            </svg>
            <span className="pdf-file-name">{file.name}</span>
            <button className="pdf-file-clear" onClick={e => { e.stopPropagation(); setFile(null); }} aria-label="Remove file">×</button>
          </div>
        ) : (
          <div className="pdf-dropzone-hint">
            <svg viewBox="0 0 24 24" fill="currentColor" width="36" height="36">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
            </svg>
            <p>Drop sermon PDF here</p>
            <span>or click to browse</span>
          </div>
        )}
      </div>

      {file && (
        <>
          <p className="pdf-lang-label">Translate into:</p>
          <div className="lang-picker">
            {Object.entries(LANG_BY_REGION).map(([region, langs]) => (
              <div key={region} className="lang-picker-group">
                <span className="lang-picker-region">{region}</span>
                <div className="lang-picker-row">
                  {langs.map(lang => (
                    <label key={lang.code} className="lang-picker-item">
                      <input type="checkbox" checked={selectedLangs.includes(lang.code)} disabled={uploading}
                        onChange={e => setSelectedLangs(prev =>
                          e.target.checked ? [...prev, lang.code] : prev.filter(c => c !== lang.code))} />
                      {lang.name}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {error && <p className="pdf-error">{error}</p>}
          <button className="btn-primary pdf-submit-btn" onClick={handleSubmit}
            disabled={!selectedLangs.length || uploading}>
            {uploading ? 'Uploading…'
              : `Translate into ${selectedLangs.length || '…'} language${selectedLangs.length !== 1 ? 's' : ''}`}
          </button>
        </>
      )}
    </div>
  );
}

// ============================================
// Translation Panel — full document translation module
// Supports PDF, DOCX, TXT, SRT with job management
// ============================================
const DOC_ACCEPT = '.pdf,.docx,.doc,.txt,.srt';
const DOC_TYPES = new Set(['pdf', 'docx', 'doc', 'txt', 'srt']);

function TranslationPanel({ onJobQueued, jobs, onDeleteJob, onUpdateJob, userGlossaryLangs }) {
  const allowedLangs = new Set(userGlossaryLangs || []);
  const { getToken } = useAuth();
  const capacityAvailable = useServiceCapacity('translation');

  // Internal sub-view state
  const [tlSub, setTlSub] = useState('upload'); // 'upload' | 'processing' | 'output'
  const [tlJobId, setTlJobId] = useState(null);
  const [tlFileName, setTlFileName] = useState(null);
  const [tlTargetLangs, setTlTargetLangs] = useState([]);
  const [tlActiveLang, setTlActiveLang] = useState(null);
  const [tlTranslations, setTlTranslations] = useState({});      // { lang: { paragraphs: [...] } }
  const [tlTransStatus, setTlTransStatus] = useState({});        // { lang: 'pending'|'completed'|'failed' }

  // Restore processing sub-view if a document job was in-flight when this panel last unmounted
  useEffect(() => {
    const inFlight = jobs.find(j => j.status === 'queued' || j.status === 'processing');
    if (inFlight) {
      setTlJobId(inFlight.jobId);
      setTlFileName(inFlight.fileName);
      setTlTargetLangs(inFlight.targetLanguages || []);
      setTlSub('processing');
    }
  }, []); // intentionally mount-only
  const [tlEditParagraphs, setTlEditParagraphs] = useState(null); // paragraphs for active lang
  const [tlSaving, setTlSaving] = useState(false);
  const [tlSaveMsg, setTlSaveMsg] = useState(null);
  const [tlAutoGlossary, setTlAutoGlossary] = useState(true);
  const [tlHasChanges, setTlHasChanges] = useState(false);
  const [tlActivePara, setTlActivePara] = useState(null);
  const [tlEditingPara, setTlEditingPara] = useState(null);
  const [tlEditText, setTlEditText] = useState('');
  const [tlOriginalUrl, setTlOriginalUrl] = useState(null);
  const [tlOriginalExt, setTlOriginalExt] = useState(null);
  const [sourceHtml, setSourceHtml] = useState('');
  const [sourceImageUrls, setSourceImageUrls] = useState({});
  const [tlLayout, setTlLayout] = useState('split'); // 'split' | 'stacked'
  const [tlCollapsedPanels, setTlCollapsedPanels] = useState({ source: false, translation: false });
  const pdfRef = useRef(null);
  const translationContentRef = useRef(null);
  const sourcePanelRef = useRef(null);
  const translationHistoryRef = useRef(null);

  // Upload form state
  const [file, setFile] = useState(null);
  const [selectedLangs, setSelectedLangs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = (f) => {
    if (!f || !capacityAvailable) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!DOC_TYPES.has(ext)) {
      setUploadError(`Unsupported file type. Accepted: PDF, DOCX, TXT, SRT`);
      return;
    }
    setUploadError(null);
    setFile(f);
  };

  const handleSubmit = async () => {
    if (!file || !selectedLangs.length || !capacityAvailable) return;
    setUploading(true);
    setUploadError(null);
    const fileType = file.name.split('.').pop().toLowerCase();
    try {
      const token = await getToken();

      // Step 1: get pre-signed S3 URL
      const urlRes = await fetch(`${API_BASE}/document/get-upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ filename: file.name, targetLanguages: selectedLangs }),
      });
      if (!urlRes.ok) {
        const err = await urlRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const { uploadUrl, jobId, blobName, fileType: resolvedFileType } = await urlRes.json();

      // Step 2: upload file directly to S3 — Content-Type must match what the API signed
      const DOC_CONTENT_TYPES = {
        pdf: 'application/pdf',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        doc: 'application/msword',
        txt: 'text/plain',
        srt: 'application/x-subrip',
      };
      const uploadContentType = DOC_CONTENT_TYPES[resolvedFileType] || file.type || 'application/octet-stream';
      const s3Res = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': uploadContentType },
        body: file,
      });
      if (!s3Res.ok) throw new Error(`S3 upload failed: ${s3Res.status}`);

      // Step 3: enqueue translation job
      const translateRes = await fetch(`${API_BASE}/document/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ jobId, blobName, targetLanguages: selectedLangs, fileType: resolvedFileType, originalName: file.name }),
      });
      if (!translateRes.ok) {
        const err = await translateRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to queue translation');
      }

      // Notify parent to add job to shared jobs list
      onJobQueued(jobId, file.name, selectedLangs, fileType);

      // Switch to processing sub-view
      setTlJobId(jobId);
      setTlFileName(file.name);
      setTlTargetLangs(selectedLangs);
      setTlTransStatus(Object.fromEntries(selectedLangs.map(l => [l, 'pending'])));
      setTlTranslations({});
      setTlActiveLang(selectedLangs[0] || null);
      setTlSub('processing');
      setFile(null);
      setSelectedLangs([]);
    } catch (e) {
      setUploadError(toUserMessage(e, 'upload'));
    } finally {
      setUploading(false);
    }
  };

  // Called by StatusPanel when job is done
  const handleTlReady = async () => {
    try {
      const res = await fetch(`${API_BASE}/status/${tlJobId}`);
      const data = await res.json();
      const translations = data.translations || {};
      setTlTransStatus(translations);
      onUpdateJob(tlJobId, { status: 'completed', translationStatus: translations });
      const firstDone = tlTargetLangs.find(l => translations[l] === 'completed') || tlTargetLangs[0];
      if (firstDone) {
        setTlActiveLang(firstDone);
        await loadTranslation(tlJobId, firstDone);
      }
    } catch (e) {}
    await Promise.all([fetchOriginalUrl(tlJobId), fetchSourceHtml(tlJobId)]);
    setTlSub('output');
  };

  // Cached by "{jobId}:{lang}", not just lang — two different documents
  // translated into the same language (e.g. both French) would otherwise
  // share one cache slot, so opening document B after A could silently
  // keep showing A's content under B's filename. Keying by job makes a
  // switch to a different document always a cache miss, regardless of
  // language overlap.
  const tlCacheKey = (jobId, lang) => `${jobId}:${lang}`;

  const loadTranslation = async (jobId, lang) => {
    const key = tlCacheKey(jobId, lang);
    if (tlTranslations[key]) return; // already cached
    try {
      const res = await fetch(`${API_BASE}/translation/${jobId}/${lang}`);
      if (!res.ok) return;
      const data = await res.json();
      setTlTranslations(prev => ({ ...prev, [key]: data }));
      setTlEditParagraphs(data.paragraphs || []);
    } catch (e) {
      // ignore
    }
  };

  const handleLangTab = async (lang) => {
    setTlActiveLang(lang);
    const key = tlCacheKey(tlJobId, lang);
    if (!tlTranslations[key]) {
      await loadTranslation(tlJobId, lang);
    } else {
      setTlEditParagraphs(tlTranslations[key]?.paragraphs || []);
    }
  };

  useEffect(() => {
    const key = tlCacheKey(tlJobId, tlActiveLang);
    if (tlActiveLang && tlTranslations[key]) {
      setTlEditParagraphs(tlTranslations[key]?.paragraphs || []);
      setTlHasChanges(false);
      setTlActivePara(null);
      setTlEditingPara(null);
    }
  }, [tlActiveLang, tlJobId, tlTranslations]);

  const fetchOriginalUrl = async (jobId) => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/document/original-url/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const { url, ext } = await res.json();
      setTlOriginalUrl(url);
      setTlOriginalExt(ext);
    } catch (_) {}
  };

  const fetchSourceHtml = async (jobId) => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/document/source/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setSourceHtml(data.html || '');
      setSourceImageUrls(data.imageUrls || {});
    } catch (_) {}
  };

  const handleTlParaClick = (idx, para) => {
    if (tlEditingPara !== null) return;
    setTlActivePara(idx);
    // Highlight the matching source element in the left panel
    if (sourcePanelRef.current) {
      sourcePanelRef.current.querySelectorAll('.tl-src-highlighted')
        .forEach(el => el.classList.remove('tl-src-highlighted'));
      const srcEl = sourcePanelRef.current.querySelector(`#src-${idx}`);
      if (srcEl) {
        srcEl.classList.add('tl-src-highlighted');
        srcEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
    // Scroll translated segment into view
    if (translationContentRef.current) {
      const activeEl = translationContentRef.current.querySelector('.segment.active');
      if (activeEl) activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const handleSourceElementClick = (event) => {
    const sourceElement = event.target.closest('[data-idx]');
    if (!sourceElement || !sourcePanelRef.current?.contains(sourceElement)) return;
    const idx = Number(sourceElement.dataset.idx);
    if (!Number.isInteger(idx) || idx < 0 || idx >= (tlEditParagraphs?.length || 0)) return;

    setTlActivePara(idx);
    sourcePanelRef.current.querySelectorAll('.tl-src-highlighted')
      .forEach(el => el.classList.remove('tl-src-highlighted'));
    sourceElement.classList.add('tl-src-highlighted');

    const translatedElement = translationContentRef.current
      ?.querySelector(`[data-para-idx="${idx}"]`);
    translatedElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const handleTlParaDoubleClick = (idx, para) => {
    setTlEditingPara(idx);
    setTlEditText(para.text);
    setTlActivePara(idx);
  };

  const handleTlSegSave = async (idx) => {
    if (tlEditingPara === null) return;
    const newParas = [...tlEditParagraphs];
    const originalText = newParas[idx].text;
    newParas[idx] = { ...newParas[idx], text: tlEditText };
    setTlEditParagraphs(newParas);
    setTlEditingPara(null);
    setTlEditText('');
    setTlHasChanges(true);

    if (tlAutoGlossary && tlActiveLang !== 'en' && tlEditText.trim() !== originalText.trim()) {
      const pair = diffWords(originalText, tlEditText);
      if (pair) {
        try {
          const token = await getToken();
          await fetch(`${API_BASE}/glossary/${tlActiveLang}/terms`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ original: pair.original, corrected: pair.corrected, category: 'Names' }),
          });
        } catch (_) {}
      }
    }
  };

  const handleTlSegCancel = () => {
    setTlEditingPara(null);
    setTlEditText('');
  };

  const handleSave = async () => {
    if (!tlJobId || !tlActiveLang || !tlEditParagraphs) return;
    setTlSaving(true);
    setTlSaveMsg(null);
    try {
      const res = await fetch(`${API_BASE}/translation/${tlJobId}/${tlActiveLang}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paragraphs: tlEditParagraphs }),
      });
      if (!res.ok) throw new Error('Save failed');

      // Auto-save word corrections to the language glossary
      if (tlAutoGlossary && tlActiveLang !== 'en') {
        const origParas = tlTranslations[tlCacheKey(tlJobId, tlActiveLang)]?.paragraphs || [];
        const pairs = [];
        tlEditParagraphs.forEach((para, i) => {
          const orig = origParas[i];
          if (!orig || para.text === orig.text) return;
          const pair = diffWords(orig.text, para.text);
          if (pair) pairs.push(pair);
        });
        if (pairs.length > 0) {
          try {
            const token = await getToken();
            const endpoint = `${API_BASE}/glossary/${tlActiveLang}/terms`;
            await Promise.all(pairs.map(p => fetch(endpoint, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ original: p.original, corrected: p.corrected, category: 'Names', mode: 'target_correction' }),
            })));
            setTlSaveMsg(`Saved · ${pairs.length} correction${pairs.length > 1 ? 's' : ''} added to glossary`);
          } catch (_) {
            setTlSaveMsg('Saved');
          }
        } else {
          setTlSaveMsg('Saved');
        }
      } else {
        setTlSaveMsg('Saved');
      }

      const saveKey = tlCacheKey(tlJobId, tlActiveLang);
      setTlTranslations(prev => ({ ...prev, [saveKey]: { ...prev[saveKey], paragraphs: tlEditParagraphs } }));
      setTlHasChanges(false);
    } catch (e) {
      setTlSaveMsg('Error saving');
    } finally {
      setTlSaving(false);
      setTimeout(() => setTlSaveMsg(null), 3000);
    }
  };

  const handleDownload = async (lang, fmt = 'docx') => {
    const token = await getToken();
    const res = await fetch(`${API_BASE}/document/download/${tlJobId}/${lang}?fmt=${fmt}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      let msg = 'Could not download this file — please try again in a moment.';
      try {
        const body = await res.json();
        if (body?.detail) msg = body.detail;
      } catch (_) { /* keep default */ }
      setTlSaveMsg(msg);
      setTimeout(() => setTlSaveMsg(null), 5000);
      return;
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="(.+?)"/);
    const filename = match ? match[1] : `translation_${lang}.${fmt}`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleReturnToHistory = () => {
    setTlSub('upload');
    // The history table is rendered only after the upload sub-view mounts.
    // Wait for that render before scrolling it into view.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        translationHistoryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  const handleSelectDocJob = async (job) => {
    setTlJobId(job.jobId);
    setTlFileName(job.fileName);
    let transStatus = job.translationStatus || {};
    // Backfill status for jobs saved before translationStatus was persisted
    // — also the source of truth when job.targetLanguages itself is empty
    // or stale (e.g. a job cached client-side from before a schema change),
    // since it reflects what the server actually has, not what the client
    // remembers requesting.
    if (job.status === 'completed') {
      try {
        const res = await fetch(`${API_BASE}/status/${job.jobId}`);
        if (res.ok) {
          const data = await res.json();
          transStatus = { ...transStatus, ...(data.translations || {}) };
          for (const lang of job.targetLanguages || []) {
            if (!transStatus[lang] && data.status === 'completed') transStatus[lang] = 'completed';
          }
        }
      } catch (_) {}
    }
    const targetLangs = job.targetLanguages?.length ? job.targetLanguages : Object.keys(transStatus);
    setTlTargetLangs(targetLangs);
    setTlTransStatus(transStatus);
    setTlTranslations({});
    setTlOriginalUrl(null);
    setTlOriginalExt(null);
    setSourceHtml('');
    setSourceImageUrls({});
    setTlHasChanges(false);
    setTlActivePara(null);
    setTlEditingPara(null);
    const firstLang = targetLangs[0];
    setTlSub('output');
    if (firstLang) {
      setTlActiveLang(firstLang);
      await Promise.all([
        loadTranslation(job.jobId, firstLang),
        fetchOriginalUrl(job.jobId),
        fetchSourceHtml(job.jobId),
      ]);
    } else {
      await Promise.all([fetchOriginalUrl(job.jobId), fetchSourceHtml(job.jobId)]);
    }
  };

  const fileTypeIcon = (ft) => {
    const icons = { pdf: 'PDF', docx: 'DOC', doc: 'DOC', txt: 'TXT', srt: 'SRT' };
    return icons[ft] || 'FILE';
  };

  return (
    <div className="translation-panel">
      {/* Sub-view: Upload */}
      {tlSub === 'upload' && (
        <div className="tl-upload-section">
          <h2 className="tl-title">Translate Document {import.meta.env.DEV && <span className="build-tag" title="Frontend build">{BUILD_TAG}</span>}</h2>
          <p className="tl-desc">
            Upload a sermon document (PDF, Word, TXT, or SRT) and select the languages to translate into.
            The translated output preserves the original document structure.
          </p>

          {!capacityAvailable && <ServicePausedBanner profile="translation" />}

          <div
            className={`pdf-dropzone${dragActive ? ' active' : ''}${file ? ' has-file' : ''}${!capacityAvailable ? ' disabled' : ''}`}
            onDragOver={capacityAvailable ? (e => { e.preventDefault(); setDragActive(true); }) : undefined}
            onDragLeave={capacityAvailable ? (() => setDragActive(false)) : undefined}
            onDrop={capacityAvailable ? (e => { e.preventDefault(); setDragActive(false); handleFile(e.dataTransfer.files[0]); }) : undefined}
            onClick={() => capacityAvailable && !file && fileInputRef.current?.click()}
            aria-disabled={!capacityAvailable}
          >
            <input ref={fileInputRef} type="file" accept={DOC_ACCEPT} style={{ display: 'none' }}
              onChange={e => handleFile(e.target.files[0])} disabled={!capacityAvailable} />
            {file ? (
              <div className="pdf-file-selected">
                <span className="tl-file-badge">{fileTypeIcon(file.name.split('.').pop().toLowerCase())}</span>
                <span className="pdf-file-name">{file.name}</span>
                <button className="pdf-file-clear" onClick={e => { e.stopPropagation(); setFile(null); setUploadError(null); }} aria-label="Remove file">×</button>
              </div>
            ) : (
              <div className="pdf-dropzone-hint">
                <svg viewBox="0 0 24 24" fill="currentColor" width="36" height="36">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                </svg>
                <p>Drop file here</p>
                <span>PDF, Word (DOCX), TXT, SRT — or click to browse</span>
              </div>
            )}
          </div>

          {file && allowedLangs.size === 0 && (
            <p className="pdf-error">You don't have any languages assigned yet. Contact your administrator to get access to translation languages.</p>
          )}
          {file && allowedLangs.size > 0 && (
            <>
              <p className="pdf-lang-label">Translate into:</p>
              <div className="lang-picker">
                {Object.entries(LANG_BY_REGION).map(([region, langs]) => {
                  const regionAllowed = langs.filter(l => allowedLangs.has(l.code));
                  if (!regionAllowed.length) return null;
                  return (
                    <div key={region} className="lang-picker-group">
                      <span className="lang-picker-region">{region}</span>
                      <div className="lang-picker-row">
                        {regionAllowed.map(lang => (
                          <label key={lang.code} className="lang-picker-item">
                            <input type="checkbox" checked={selectedLangs.includes(lang.code)} disabled={uploading}
                              onChange={e => setSelectedLangs(prev =>
                                e.target.checked ? [...prev, lang.code] : prev.filter(c => c !== lang.code))} />
                            {lang.name}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {uploadError && <p className="pdf-error">{uploadError}</p>}
          {file && (
            <button className="btn-primary pdf-submit-btn" onClick={handleSubmit}
              disabled={!selectedLangs.length || uploading || !capacityAvailable}>
              {uploading ? 'Uploading…'
                : !capacityAvailable ? 'Service paused'
                : selectedLangs.length
                  ? `Translate into ${selectedLangs.length} language${selectedLangs.length !== 1 ? 's' : ''}`
                  : 'Select languages above'}
            </button>
          )}
        </div>
      )}

      {/* Sub-view: Processing */}
      {tlSub === 'processing' && (
        <div className="tl-processing-section">
          <StatusPanel
            jobId={tlJobId}
            fileName={tlFileName}
            onTranscriptReady={handleTlReady}
            onStatusUpdate={() => {}}
            mode="translation"
            targetLanguages={tlTargetLangs}
            onStartOver={() => setTlSub('upload')}
          />
          <button className="btn-secondary tl-back-btn" style={{marginTop: 16}}
            onClick={() => setTlSub('upload')}>
            ← Back to upload
          </button>
        </div>
      )}

      {/* Sub-view: Output — mirrors TranscriptEditor layout */}
      {tlSub === 'output' && (
        <div className="transcript-editor">
          <div className="editor-header">
            <div className="editor-header-top tl-review-heading">
              <div className="tl-review-title-group">
                <button className="tl-doc-back-link" onClick={handleReturnToHistory}>
                  <span aria-hidden="true">←</span> Translation Dashboard
                </button>
                <h2 title={tlFileName}>{tlFileName}</h2>
              </div>
              <div
                className="editor-hint tl-review-help"
                title="Click a paragraph to locate its match. Double-click translated text to edit it."
              >
                <span aria-hidden="true">?</span>
                Review help
              </div>
            </div>
            <div className="editor-header-controls">
              {/* Language tabs */}
              <div className="tl-control-group tl-language-group" aria-label="Translation languages">
                <span className="tl-control-label">Language</span>
                <div className="tl-lang-tabs">
                {tlTargetLangs.map(lang => (
                  <button
                    key={lang}
                    className={`tl-lang-tab${tlActiveLang === lang ? ' active' : ''}${tlTransStatus[lang] === 'failed' ? ' failed' : ''}`}
                    onClick={() => handleLangTab(lang)}
                  >
                    {LANG_MAP[lang] || lang}
                    {tlTransStatus[lang] === 'pending' && <span className="tl-tab-spinner"> ⏳</span>}
                    {tlTransStatus[lang] === 'failed' && <span style={{color:'#ef4444'}}> ✗</span>}
                  </button>
                ))}
                </div>
              </div>
              {tlActiveLang && tlTransStatus[tlActiveLang] === 'completed' && (
                <div className="tl-control-group tl-export-group" aria-label="Download translation">
                  <span className="tl-control-label">Export</span>
                  <div className="tl-download-group">
                    <button className="btn-secondary" onClick={() => handleDownload(tlActiveLang, 'docx')}>DOC</button>
                    <button className="btn-secondary" onClick={() => handleDownload(tlActiveLang, 'pdf')}>PDF</button>
                    <button className="btn-secondary" onClick={() => handleDownload(tlActiveLang, 'txt')}>TXT</button>
                    {tlOriginalExt === 'srt' && (
                      <button className="btn-secondary" onClick={() => handleDownload(tlActiveLang, 'srt')}>SRT</button>
                    )}
                  </div>
                </div>
              )}
              {tlHasChanges && (
                <button className="btn-primary" onClick={handleSave} disabled={tlSaving}>
                  {tlSaving ? 'Saving…' : 'Save Changes'}
                </button>
              )}
              {tlSaveMsg && <span className="tl-save-msg">{tlSaveMsg}</span>}
              <div className="tl-review-actions">
                <button
                  className="btn-secondary tl-layout-toggle"
                  title={tlLayout === 'split' ? 'Switch to stacked view' : 'Switch to side-by-side view'}
                  onClick={() => {
                    setTlLayout(l => l === 'split' ? 'stacked' : 'split');
                    setTlCollapsedPanels({ source: false, translation: false });
                  }}
                >
                  {tlLayout === 'split' ? '⇅ Stack' : '⇄ Split'}
                </button>
                <button className="btn-secondary tl-back-btn" onClick={() => setTlSub('upload')}>
                  + New document
                </button>
              </div>
            </div>
          </div>

          {/* Side-by-side review: source HTML left, translated segments right */}
          {tlActiveLang && tlEditParagraphs?.length > 0 && (
            <div className={`tl-review-split tl-layout-${tlLayout}`}>
              {/* LEFT — source document rendered as HTML */}
              <div className={`tl-source-panel${tlCollapsedPanels.source ? ' tl-panel-collapsed' : ''}`} ref={sourcePanelRef}>
                <div className="tl-panel-label">
                  <span>Original</span>
                  <button
                    className="tl-panel-collapse"
                    onClick={() => setTlCollapsedPanels(p => ({ ...p, source: !p.source }))}
                    aria-expanded={!tlCollapsedPanels.source}
                  >
                    {tlCollapsedPanels.source ? 'Expand' : 'Collapse'}
                  </button>
                </div>
                {sourceHtml
                  ? <div
                      className="tl-source-content"
                      onClick={handleSourceElementClick}
                      dangerouslySetInnerHTML={{ __html: sourceHtml }}
                    />
                  : <p className="tl-src-loading">Loading source document…</p>
                }
              </div>

              <div className="tl-split-divider" />

              {/* RIGHT — translated segments */}
              <div className={`tl-translated-panel${tlCollapsedPanels.translation ? ' tl-panel-collapsed' : ''}`}>
                <div className="tl-panel-label">
                  <span>{LANG_MAP[tlActiveLang] || tlActiveLang} — Translation</span>
                  <button
                    className="tl-panel-collapse"
                    onClick={() => setTlCollapsedPanels(p => ({ ...p, translation: !p.translation }))}
                    aria-expanded={!tlCollapsedPanels.translation}
                  >
                    {tlCollapsedPanels.translation ? 'Expand' : 'Collapse'}
                  </button>
                </div>
                <div className="transcript-content" ref={translationContentRef}>
                  {tlEditParagraphs.map((para, idx) => {
                    if (para.type === 'image') {
                      const imgUrl = sourceImageUrls[String(para.para_idx)];
                      return (
                        <div
                          key={idx}
                          data-para-idx={idx}
                          className={`tl-img-placeholder${tlActivePara === idx ? ' tl-corresponding-active' : ''}`}
                        >
                          {imgUrl
                            ? <img src={imgUrl} alt="Document image" style={{maxWidth:'100%',borderRadius:4}} />
                            : <span style={{color:'#9ca3af',fontSize:13}}>🖼 image</span>
                          }
                        </div>
                      );
                    }
                    if (para.type === 'cover_image') {
                      // Cover pages are a single rendered image with the title
                      // text translated in place — not editable line-by-line
                      // like body paragraphs, same as regular images above.
                      const imgUrl = tlTranslations[tlCacheKey(tlJobId, tlActiveLang)]?.imageUrls?.[String(para.para_idx)];
                      return (
                        <div
                          key={idx}
                          data-para-idx={idx}
                          className={`tl-img-placeholder${tlActivePara === idx ? ' tl-corresponding-active' : ''}`}
                        >
                          {imgUrl
                            ? <img src={imgUrl} alt="Translated cover page" style={{maxWidth:'100%',borderRadius:4}} />
                            : <span style={{color:'#9ca3af',fontSize:13}}>🖼 cover page</span>
                          }
                        </div>
                      );
                    }
                    return (
                      <div key={idx} className="paragraph" data-para-idx={idx}>
                        <TranslationSegment
                          para={para}
                          isActive={tlActivePara === idx}
                          isEditing={tlEditingPara === idx}
                          editText={tlEditText}
                          setEditText={setTlEditText}
                          onClick={() => handleTlParaClick(idx, para)}
                          onDoubleClick={() => handleTlParaDoubleClick(idx, para)}
                          onSave={() => handleTlSegSave(idx)}
                          onCancel={handleTlSegCancel}
                          saveToGlossary={tlAutoGlossary}
                          onToggleGlossary={setTlAutoGlossary}
                          lang={tlActiveLang}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <button
            className="tl-back-to-top"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            aria-label="Back to top"
            title="Back to top"
          >
            ↑
          </button>

          {tlActiveLang && tlTransStatus[tlActiveLang] === 'pending' && (
            <p className="tl-lang-pending">Translation for {LANG_MAP[tlActiveLang] || tlActiveLang} is still processing…</p>
          )}
          {tlActiveLang && tlTransStatus[tlActiveLang] === 'failed' && (
            <div className="tl-lang-failed-block">
              <p className="tl-lang-failed">Translation for {LANG_MAP[tlActiveLang] || tlActiveLang} did not finish.</p>
              <button className="btn-secondary" onClick={() => setTlSub('upload')}>Start Over</button>
            </div>
          )}
        </div>
      )}

      {/* Job History — only on the upload sub-view, same as the
          transcription page keeps its job list off the editor view
          entirely. Reviewing a document previously stayed on the same
          page as this table, squeezing both into shared vertical space. */}
      {tlSub === 'upload' && jobs && jobs.length > 0 && (
        <div className="tl-job-history" ref={translationHistoryRef}>
          <h3 className="tl-history-title">Document History</h3>
          <table className="user-mgmt-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Type</th>
                <th>Languages</th>
                <th>Status</th>
                <th>Time</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => (
                <tr key={job.jobId} className={job.status === 'queued' || job.status === 'processing' ? 'user-row--pending' : ''}>
                  <td className="user-email" title={job.fileName}>{job.fileName}</td>
                  <td><span className="tl-file-badge">{fileTypeIcon(job.fileType)}</span></td>
                  <td>{(job.targetLanguages || []).map(l => LANG_MAP[l] || l).join(', ')}</td>
                  <td>
                    <span className={`status-badge status-${job.status || 'queued'}`}>
                      {job.status === 'completed' ? 'Completed' : job.status === 'failed' ? 'Failed' : job.status === 'processing' ? 'Processing' : 'Queued'}
                    </span>
                  </td>
                  <td style={{whiteSpace:'nowrap'}}>
                    {job.processingTime != null
                      ? job.processingTime >= 60
                        ? `${Math.round(job.processingTime / 60)} min`
                        : `${Math.round(job.processingTime)} s`
                      : '—'}
                  </td>
                  <td style={{whiteSpace:'nowrap'}}>
                    {job.createdAt ? new Date(job.createdAt).toLocaleString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit'
                    }) : '—'}
                  </td>
                  <td style={{display:'flex',gap:4}}>
                    {job.status === 'completed' && (
                      <button className="btn-secondary" style={{fontSize:12,padding:'2px 8px'}}
                        onClick={() => handleSelectDocJob(job)}>
                        View
                      </button>
                    )}
                    <button className="btn-secondary" style={{fontSize:12,padding:'2px 8px',color:'#ef4444'}}
                      onClick={() => onDeleteJob(job.jobId)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================
// Translate Panel — shown in editor sidebar after review
// ============================================
function TranslatePanel({
  jobId,
  translationStatusMap,
  translationQueuedAtMap,
  onTranslationQueued,
  userGlossaryLangs,
}) {
  const { getToken } = useAuth();
  const capacityAvailable = useServiceCapacity('translation');
  const [selectedLangs, setSelectedLangs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());

  const pendingOrDone = new Set(
    Object.entries(translationStatusMap)
      .filter(([, status]) => status === 'pending' || status === 'completed')
      .map(([lang]) => lang)
  );
  const allowedLangs = new Set(userGlossaryLangs || []);
  const statusEntries = Object.entries(translationStatusMap);
  const hasPending = statusEntries.some(([, status]) => status === 'pending');

  useEffect(() => {
    if (!hasPending) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [hasPending]);

  const elapsedLabel = (lang) => {
    const started = translationQueuedAtMap?.[lang];
    if (!started) return 'In progress';
    const elapsed = Math.max(0, Math.floor((now - new Date(started).getTime()) / 1000));
    const hours = Math.floor(elapsed / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    const seconds = elapsed % 60;
    return hours
      ? `${hours}h ${minutes}m elapsed`
      : `${minutes}m ${String(seconds).padStart(2, '0')}s elapsed`;
  };

  const handleSubmit = async () => {
    if (!selectedLangs.length || !capacityAvailable) return;
    setSubmitting(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/translation/${jobId}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ targetLanguages: selectedLangs }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to queue translation');
      }
      onTranslationQueued(selectedLangs);
      setSelectedLangs([]);
    } catch (e) {
      setError(toUserMessage(e, 'translation'));
    } finally {
      setSubmitting(false);
    }
  };

  const availableLangs = TRANSLATION_LANGUAGES.filter(l => allowedLangs.has(l.code) && !pendingOrDone.has(l.code));

  if (allowedLangs.size === 0) {
    return (
      <div className="translate-panel">
        <h3 className="translate-panel-title">Translate</h3>
        <p className="translate-panel-hint">
          You don't have any languages assigned yet. Contact your administrator to get access to translation languages.
        </p>
      </div>
    );
  }

  return (
    <div className="translate-panel">
      <h3 className="translate-panel-title">Translate this transcript</h3>
      <p className="translate-panel-hint">
        Translations use the reviewed English transcript and your assigned glossary.
      </p>

      {statusEntries.length > 0 && (
        <div className="transcript-translation-status" aria-live="polite">
          {statusEntries.map(([lang, status]) => (
            <div key={lang} className={`translation-status-row status-${status}`}>
              <span className="translation-status-icon" aria-hidden="true">
                {status === 'completed' ? '✓' : status === 'failed' ? '!' : ''}
              </span>
              <span className="translation-status-copy">
                <strong>{LANG_MAP[lang] || lang.toUpperCase()}</strong>
                <small>
                  {status === 'completed'
                    ? 'Ready to review'
                    : status === 'failed'
                      ? 'Translation failed — select it below to retry'
                      : elapsedLabel(lang)}
                </small>
              </span>
              <span className="translation-status-label">
                {status === 'completed' ? 'Ready' : status === 'failed' ? 'Failed' : 'Translating'}
              </span>
            </div>
          ))}
          {hasPending && (
            <p className="translation-long-note">
              Long sermons can take a while. You may leave this page; processing continues in the background.
            </p>
          )}
        </div>
      )}

      {availableLangs.length > 0 && (
        <>
          {!capacityAvailable && <ServicePausedBanner profile="translation" />}
          <p className="translate-panel-section-label">
            {statusEntries.length ? 'Add another language' : 'Choose languages'}
          </p>
          <div className="lang-picker">
        {Object.entries(LANG_BY_REGION).map(([region, langs]) => {
          const regionAvailable = langs.filter(l => allowedLangs.has(l.code) && !pendingOrDone.has(l.code));
          if (!regionAvailable.length) return null;
          return (
            <div key={region} className="lang-picker-group">
              <span className="lang-picker-region">{region}</span>
              <div className="lang-picker-row">
                {regionAvailable.map(lang => (
                  <label key={lang.code} className="lang-picker-item">
                    <input
                      type="checkbox"
                      checked={selectedLangs.includes(lang.code)}
                      onChange={e => setSelectedLangs(prev =>
                        e.target.checked ? [...prev, lang.code] : prev.filter(c => c !== lang.code)
                      )}
                      disabled={submitting}
                    />
                    {lang.name}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
          </div>
          {error && <p className="translate-panel-error">{error}</p>}
          <button
            className="btn-primary translate-panel-btn"
            onClick={handleSubmit}
            disabled={!selectedLangs.length || submitting || !capacityAvailable}
          >
            {submitting ? 'Queuing…'
              : !capacityAvailable ? 'Service paused'
              : `Start translation${selectedLangs.length ? ` (${selectedLangs.length})` : ''}`}
          </button>
        </>
      )}
    </div>
  );
}

function ExportOptions({ transcript, jobId, onBackToDashboard }) {
  const [exporting, setExporting] = useState(false);

  const generateText = (includeTimestamps) => {
    if (!transcript?.paragraphs) return '';
    return transcript.paragraphs.map(para => {
      if (includeTimestamps) {
        return para.segments.map(seg => `${seg.ts}  ${seg.text}`).join(' ') + `  ${para.end_ts}`;
      } else {
        return para.segments.map(seg => seg.text).join(' ');
      }
    }).join('\n\n');
  };

  const generateSRT = () => {
    if (!transcript?.paragraphs) return '';
    let index = 1;
    let srt = '';
    
    transcript.paragraphs.forEach(para => {
      para.segments.forEach(seg => {
        const startTime = formatSRTTime(seg.start);
        const endTime = formatSRTTime(seg.end);
        srt += `${index}\n${startTime} --> ${endTime}\n${seg.text}\n\n`;
        index++;
      });
    });
    
    return srt;
  };

  const formatSRTTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 1000);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')},${ms.toString().padStart(3, '0')}`;
  };

  const downloadText = (includeTimestamps) => {
    const text = generateText(includeTimestamps);
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${jobId}${includeTimestamps ? '_timestamped' : ''}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadSRT = () => {
    const srt = generateSRT();
    const blob = new Blob([srt], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${jobId}.srt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadDocx = (includeTimestamps) => {
    setExporting(true);
    
    // Generate DOCX-compatible HTML
    const content = transcript.paragraphs.map(para => {
      if (includeTimestamps) {
        const text = para.segments.map(seg => 
          `<span style="color:#888;font-size:9pt">${seg.ts}</span> ${seg.text}`
        ).join(' ');
        return `<p style="font-family:Georgia;font-size:12pt;line-height:1.8">${text} <span style="color:#888;font-size:9pt">${para.end_ts}</span></p>`;
      } else {
        const text = para.segments.map(seg => seg.text).join(' ');
        return `<p style="font-family:Georgia;font-size:12pt;line-height:1.8">${text}</p>`;
      }
    }).join('');

    const html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
      <head><meta charset="utf-8"><title>Transcript</title></head>
      <body>${content}</body>
      </html>
    `;

    const blob = new Blob([html], { type: 'application/vnd.ms-word' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${jobId}${includeTimestamps ? '_timestamped' : ''}.doc`;
    a.click();
    URL.revokeObjectURL(url);
    setExporting(false);
  };

  return (
    <div className="export-options">
      <h3>Export</h3>
      
      <div className="export-group">
        <span className="export-label">With Timestamps</span>
        <div className="export-buttons">
          <button onClick={() => downloadText(true)} disabled={exporting}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            TXT
          </button>
          <button onClick={() => downloadDocx(true)} disabled={exporting}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M6,2H14L20,8V20A2,2 0 0,1 18,22H6A2,2 0 0,1 4,20V4A2,2 0 0,1 6,2M13,3.5V9H18.5L13,3.5M7,13L8.5,18H10.5L12,14L13.5,18H15.5L17,13H15.5L14.5,17L13,13.5H11L9.5,17L8.5,13H7Z"/>
            </svg>
            Word
          </button>
        </div>
      </div>

      <div className="export-group">
        <span className="export-label">Without Timestamps</span>
        <div className="export-buttons">
          <button onClick={() => downloadText(false)} disabled={exporting}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            TXT
          </button>
          <button onClick={() => downloadDocx(false)} disabled={exporting}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M6,2H14L20,8V20A2,2 0 0,1 18,22H6A2,2 0 0,1 4,20V4A2,2 0 0,1 6,2M13,3.5V9H18.5L13,3.5M7,13L8.5,18H10.5L12,14L13.5,18H15.5L17,13H15.5L14.5,17L13,13.5H11L9.5,17L8.5,13H7Z"/>
            </svg>
            Word
          </button>
        </div>
      </div>

      <div className="export-group">
        <span className="export-label">Subtitles</span>
        <div className="export-buttons">
          <button onClick={downloadSRT} disabled={exporting}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="4" width="20" height="16" rx="2"/>
              <line x1="6" y1="12" x2="18" y2="12"/>
              <line x1="6" y1="16" x2="14" y2="16"/>
            </svg>
            SRT
          </button>
        </div>
      </div>

      <div className="how-to-edit">
        <h4>How to Edit</h4>
        <ul>
          <li><strong>Single click</strong> - Play audio from that point</li>
          <li><strong>Double click</strong> - Edit the text</li>
          <li><strong>Save Changes</strong> - Save all edits</li>
        </ul>
      </div>

      <button className="btn-back-dashboard" onClick={onBackToDashboard}>
        ← Back to Dashboard
      </button>
    </div>
  );
}

// ============================================
// Analytics Page Component
// ============================================
const VIDEO_EXT_SET = new Set(['mp4', 'mov', 'mkv', 'avi', 'webm', 'm4v']);

function BarChartSVG({ data }) {
  const maxVal = Math.max(...data.map(d => d.count), 1);
  const W = 420, H = 160, PAD_L = 28, PAD_B = 28, PAD_T = 16, PAD_R = 8;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_B - PAD_T;
  const barW = Math.max(4, chartW / data.length - 6);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ overflow: 'visible' }}>
      {/* Y-axis line */}
      <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PAD_T + chartH} stroke="#E5DFD6" strokeWidth="1" />
      {/* Baseline */}
      <line x1={PAD_L} y1={PAD_T + chartH} x2={W - PAD_R} y2={PAD_T + chartH} stroke="#E5DFD6" strokeWidth="1" />
      {data.map((d, i) => {
        const slotW = chartW / data.length;
        const barH = maxVal > 0 ? (d.count / maxVal) * chartH : 0;
        const x = PAD_L + i * slotW + (slotW - barW) / 2;
        const y = PAD_T + chartH - barH;
        return (
          <g key={d.month}>
            {d.count > 0 && (
              <rect x={x} y={y} width={barW} height={barH} fill="#8B5A2B" rx="3" />
            )}
            {d.count === 0 && (
              <rect x={x} y={PAD_T + chartH - 2} width={barW} height={2} fill="#D4C4B0" rx="1" />
            )}
            {d.count > 0 && (
              <text x={x + barW / 2} y={y - 4} textAnchor="middle" fontSize="10" fill="#8B5A2B" fontWeight="600">
                {d.count}
              </text>
            )}
            <text x={x + barW / 2} y={H - 4} textAnchor="middle" fontSize="10" fill="#9C8B78">
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function DonutChartSVG({ audio, video }) {
  const total = audio + video;
  if (total === 0) {
    return (
      <svg viewBox="0 0 160 160" width="160" height="160">
        <circle cx="80" cy="80" r="52" fill="none" stroke="#E5DFD6" strokeWidth="28" />
        <text x="80" y="80" textAnchor="middle" dominantBaseline="middle" fontSize="22" fontWeight="700" fill="#9C8B78">0</text>
        <text x="80" y="98" textAnchor="middle" fontSize="11" fill="#9C8B78">total</text>
      </svg>
    );
  }
  const cx = 80, cy = 80, r = 52, strokeW = 28;
  const circ = 2 * Math.PI * r;
  const audioDash = (audio / total) * circ;
  const videoDash = (video / total) * circ;
  return (
    <svg viewBox="0 0 160 160" width="160" height="160">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#8B5A2B" strokeWidth={strokeW}
        strokeDasharray={`${audioDash} ${circ - audioDash}`}
        strokeDashoffset={circ * 0.25}
        style={{ transition: 'stroke-dasharray 0.6s ease' }} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#D4A574" strokeWidth={strokeW}
        strokeDasharray={`${videoDash} ${circ - videoDash}`}
        strokeDashoffset={circ * 0.25 - audioDash}
        style={{ transition: 'stroke-dasharray 0.6s ease' }} />
      <text x={cx} y={cy - 6} textAnchor="middle" dominantBaseline="middle" fontSize="22" fontWeight="700" fill="#2C2416">{total}</text>
      <text x={cx} y={cy + 14} textAnchor="middle" fontSize="11" fill="#9C8B78">total</text>
    </svg>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="stat-card">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

// ============================================
// User Management Panel (Admin only)
// ============================================

function accessBadge(access, role) {
  if (role === 'super_admin') return <span className="access-badge access-badge--admin">Super Admin</span>;
  if (role === 'admin') return <span className="access-badge access-badge--admin">Admin</span>;
  if (access === 'full') return <span className="access-badge access-badge--full">Full</span>;
  if (access === 'translation') return <span className="access-badge access-badge--translation">Translation</span>;
  if (access === 'transcription') return <span className="access-badge access-badge--transcription">Transcription</span>;
  return <span className="access-badge access-badge--none">Pending</span>;
}

function UserManagementPanel({ isSuperAdmin }) {
  const { getToken } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editState, setEditState] = useState({});  // { userId: { access, role, glossary_langs, dirty, saving } }

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setUsers(data);
      // Init edit state from server values
      const init = {};
      data.forEach(u => {
        init[u.id] = { access: u.access || '', role: u.role || '', glossary_langs: u.glossary_langs || [], dirty: false, saving: false };
      });
      setEditState(init);
    } catch (e) {
      setError(toUserMessage(e, 'admin'));
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const setField = (userId, field, value) => {
    setEditState(prev => ({
      ...prev,
      [userId]: { ...prev[userId], [field]: value, dirty: true },
    }));
  };

  const toggleLang = (userId, langCode, checked) => {
    setEditState(prev => {
      const cur = prev[userId]?.glossary_langs || [];
      const next = checked ? [...cur, langCode] : cur.filter(l => l !== langCode);
      return { ...prev, [userId]: { ...prev[userId], glossary_langs: next, dirty: true } };
    });
  };

  const handleSave = async (userId) => {
    const es = editState[userId];
    if (!es) return;
    setEditState(prev => ({ ...prev, [userId]: { ...prev[userId], saving: true, error: null } }));
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/admin/users/${userId}/role`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: es.role, access: es.access, glossary_langs: es.glossary_langs }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const updated = await r.json();
      setUsers(prev => prev.map(u => u.id === userId
        ? { ...u, role: updated.role, access: updated.access, glossary_langs: updated.glossary_langs }
        : u));
      setEditState(prev => ({ ...prev, [userId]: { ...prev[userId], dirty: false, saving: false,
        role: updated.role, access: updated.access, glossary_langs: updated.glossary_langs } }));
    } catch (e) {
      setEditState(prev => ({ ...prev, [userId]: { ...prev[userId], saving: false, error: toUserMessage(e, 'admin') } }));
    }
  };

  if (loading) return <div className="analytics-loading"><div className="analytics-spinner" /><p>Loading users…</p></div>;
  if (error) return <div className="analytics-error"><p>{error}</p></div>;

  return (
    <div className="user-mgmt">
      <div className="analytics-header">
        <h2 className="analytics-title">User Management</h2>
        <p className="analytics-sub">{users.length} registered {users.length === 1 ? 'user' : 'users'}</p>
      </div>
      <div className="user-mgmt-table-wrap">
        <table className="user-mgmt-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Access</th>
              <th>Glossary Languages</th>
              <th>Joined</th>
              <th>Last sign-in</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const es = editState[u.id] || { access: u.access || '', role: u.role || '', glossary_langs: u.glossary_langs || [], dirty: false, saving: false };
              const showLangPicker = es.access === 'translation' || es.access === 'full';
              const isPending = !es.access && !es.role;
              return (
                <tr key={u.id} className={isPending ? 'user-row--pending' : ''}>
                  <td>{u.name || '—'}</td>
                  <td className="user-email">{u.email}</td>
                  <td>
                    <div className="user-access-editor">
                      <select
                        className="user-role-select"
                        value={es.access}
                        disabled={es.saving}
                        onChange={e => setField(u.id, 'access', e.target.value)}
                      >
                        <option value="">Pending</option>
                        <option value="transcription">Transcription only</option>
                        <option value="translation">Translation only</option>
                        <option value="full">Full (both)</option>
                      </select>
                      {isSuperAdmin && (
                        <label style={{display:'flex',alignItems:'center',gap:6,fontSize:'0.82rem',color:'#64748b'}}>
                          <input type="checkbox" checked={es.role === 'admin'}
                            disabled={es.saving}
                            onChange={e => setField(u.id, 'role', e.target.checked ? 'admin' : '')} />
                          Grant Admin role
                        </label>
                      )}
                      {showLangPicker && (
                        <div className="user-lang-picker">
                          {TRANSLATION_LANGUAGES.map(l => (
                            <label key={l.code}>
                              <input type="checkbox"
                                checked={es.glossary_langs.includes(l.code)}
                                disabled={es.saving || (es.access === 'full' && l.code === 'en')}
                                onChange={e => toggleLang(u.id, l.code, e.target.checked)} />
                              {l.name}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    {accessBadge(u.access, u.role)}
                    <div style={{marginTop:4}}>
                      {(u.glossary_langs || []).map(l => (
                        <span key={l} className="lang-chip">{LANG_MAP[l] || l}</span>
                      ))}
                    </div>
                  </td>
                  <td>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                  <td>{u.last_sign_in ? new Date(u.last_sign_in).toLocaleDateString() : 'Never'}</td>
                  <td>
                    {es.dirty && (
                      <button className="btn-primary" style={{fontSize:'0.8rem',padding:'4px 12px'}}
                        disabled={es.saving} onClick={() => handleSave(u.id)}>
                        {es.saving ? 'Saving…' : 'Save'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================
// Admin Panel (tabs: Analytics | Observability | Users | Service Status)
// ============================================
function AdminPanel({ isSuperAdmin }) {
  const [tab, setTab] = useState('analytics');
  return (
    <div className="admin-panel">
      <div className="admin-tabs">
        <button
          className={`admin-tab${tab === 'analytics' ? ' admin-tab--active' : ''}`}
          onClick={() => setTab('analytics')}
        >
          Analytics
        </button>
        <button
          className={`admin-tab${tab === 'observability' ? ' admin-tab--active' : ''}`}
          onClick={() => setTab('observability')}
        >
          Observability
        </button>
        <button
          className={`admin-tab${tab === 'users' ? ' admin-tab--active' : ''}`}
          onClick={() => setTab('users')}
        >
          Users
        </button>
        <button
          className={`admin-tab${tab === 'service-status' ? ' admin-tab--active' : ''}`}
          onClick={() => setTab('service-status')}
        >
          Service Status
        </button>
      </div>
      {tab === 'analytics' && <AnalyticsPage />}
      {tab === 'observability' && <ObservabilityPage />}
      {tab === 'users' && <UserManagementPanel isSuperAdmin={isSuperAdmin} />}
      {tab === 'service-status' && <ServiceStatusPanel />}
    </div>
  );
}

// ============================================
// Service Status — admin kill-switch for transcription/translation GPU
// availability, without touching GPU infrastructure or server config.
// ============================================
function ServiceStatusPanel() {
  const { getToken } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState('');
  const [error, setError] = useState('');

  const authFetch = useCallback(async (path, options = {}) => {
    const token = await getToken();
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }, [getToken]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setStatus(await authFetch('/admin/service-status'));
    } catch (e) {
      setError(toUserMessage(e, 'admin'));
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const toggle = useCallback(async (profile, enabled) => {
    setPending(profile);
    setError('');
    try {
      setStatus(await authFetch('/admin/service-status', {
        method: 'POST',
        body: JSON.stringify({ profile, enabled }),
      }));
    } catch (e) {
      setError(toUserMessage(e, 'admin'));
    } finally {
      setPending('');
    }
  }, [authFetch]);

  if (loading) return <div className="admin-loading">Loading service status…</div>;

  const transcriptionOn = status?.transcription !== false;
  const translationOn = status?.translation !== false;
  const bothOn = transcriptionOn && translationOn;

  const rows = [
    {
      key: 'global',
      label: 'Global',
      hint: 'Turns transcription and translation on or off together.',
      on: bothOn,
      onToggle: () => toggle('global', !bothOn),
    },
    {
      key: 'transcription',
      label: 'Transcription',
      hint: 'Audio/video upload and transcription.',
      on: transcriptionOn,
      onToggle: () => toggle('transcription', !transcriptionOn),
    },
    {
      key: 'translation',
      label: 'Translation',
      hint: 'Document translation and transcript translation.',
      on: translationOn,
      onToggle: () => toggle('translation', !translationOn),
    },
  ];

  return (
    <div className="service-status-panel">
      <p className="service-status-intro">
        Pause or resume GPU-backed processing for users. Takes effect within about 30 seconds — no server changes needed.
      </p>
      {error && <p className="status-error-msg">{error}</p>}
      <div className="service-status-rows">
        {rows.map((row) => (
          <div className="service-status-row" key={row.key}>
            <div>
              <div className="service-status-row-label">{row.label}</div>
              <div className="service-status-row-hint">{row.hint}</div>
            </div>
            <button
              type="button"
              className={`service-status-toggle${row.on ? ' on' : ' off'}`}
              role="switch"
              aria-checked={row.on}
              aria-label={`${row.label}: ${row.on ? 'enabled' : 'disabled'}`}
              disabled={pending === row.key}
              onClick={row.onToggle}
            >
              <span className="service-status-toggle-knob" />
              <span className="service-status-toggle-text">{row.on ? 'On' : 'Off'}</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ObservabilityPage() {
  const { getToken } = useAuth();
  const [summary, setSummary] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [costs, setCosts] = useState(null);
  const [costPeriod, setCostPeriod] = useState('month');
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');

  const authFetch = useCallback(async (path) => {
    const token = await getToken();
    const response = await fetch(`${API_BASE}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }, [getToken]);

  const loadOverview = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setError('');
    try {
      const suffix = filter ? `?status=${encodeURIComponent(filter)}&limit=100` : '?limit=100';
      const [nextSummary, nextTasks, nextCosts] = await Promise.all([
        authFetch('/admin/observability/summary'),
        authFetch(`/admin/observability/tasks${suffix}`),
        authFetch(`/admin/observability/costs?period=${costPeriod}`),
      ]);
      setSummary(nextSummary);
      setTasks(nextTasks.items || []);
      setCosts(nextCosts);
    } catch (e) {
      setError(toUserMessage(e, 'admin'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [authFetch, filter, costPeriod]);

  useEffect(() => {
    loadOverview();
    const timer = setInterval(() => loadOverview(true), 15000);
    return () => clearInterval(timer);
  }, [loadOverview]);

  const openTask = useCallback(async (task) => {
    setSelected(task);
    setDetail(null);
    setDetailLoading(true);
    try {
      setDetail(await authFetch(`/admin/observability/tasks/${encodeURIComponent(task.job_id)}`));
    } catch (e) {
      setError(toUserMessage(e, 'admin'));
    } finally {
      setDetailLoading(false);
    }
  }, [authFetch]);

  if (loading) {
    return <div className="analytics-loading"><div className="analytics-spinner" /><p>Loading system activity…</p></div>;
  }

  const counts = summary?.taskCounts || {};
  const healthCopy = summary?.health === 'attention'
    ? `${counts.failed || 0} task${counts.failed === 1 ? '' : 's'} need attention`
    : summary?.health === 'busy'
      ? 'Work is moving through the system'
      : 'All systems are operating normally';
  const healthHint = summary?.health === 'attention'
    ? 'Open Failed to see the affected stage and attempt.'
    : `Transcription queue: ${summary?.queues?.transcription || 0} · Translation queue: ${summary?.queues?.translation || 0}`;

  const cards = [
    ['processing', 'Active', counts.processing || 0],
    ['queued', 'Waiting', counts.queued || 0],
    ['failed', 'Failed', counts.failed || 0],
    ['completed', 'Completed', counts.completed || 0],
  ];

  return (
    <div className="observability-page">
      <div className="obs-breadcrumbs">
        <button onClick={() => { setSelected(null); setDetail(null); }}>Observability</button>
        {selected && <><span aria-hidden="true">/</span><span>{selected.job_id}</span></>}
      </div>

      {selected ? (
        <TaskTraceDetail
          task={selected}
          detail={detail}
          loading={detailLoading}
          onBack={() => { setSelected(null); setDetail(null); }}
        />
      ) : (
        <>
          <div className={`obs-health obs-health--${summary?.health || 'healthy'}`} role="status">
            <div>
              <span className="obs-health__eyebrow">System health</span>
              <h2>{healthCopy}</h2>
              <p>{healthHint}</p>
            </div>
            <div className="obs-health__updated">
              Updated {summary?.generatedAt ? new Date(summary.generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'just now'}
            </div>
          </div>

          {error && <div className="analytics-error"><p>{error}</p></div>}

          <div className="obs-stat-grid" aria-label="Task status summary">
            {cards.map(([status, label, value]) => (
              <button
                key={status}
                className={`obs-stat-card${filter === status ? ' obs-stat-card--active' : ''}`}
                onClick={() => setFilter(filter === status ? '' : status)}
                aria-pressed={filter === status}
              >
                <span>{label}</span>
                <strong>{value.toLocaleString()}</strong>
                <small>View {label.toLowerCase()} tasks</small>
              </button>
            ))}
          </div>

          <section className="obs-panel obs-cost-panel">
            <div className="obs-panel__header">
              <div>
                <h3>Estimated GPU cost</h3>
                <p>Processing, startup, and warm idle before automatic shutdown.</p>
              </div>
              <div className="obs-period-picker" aria-label="Cost period">
                {['today', 'week', 'month', 'all'].map(period => (
                  <button key={period} className={costPeriod === period ? 'active' : ''} onClick={() => setCostPeriod(period)}>
                    {period === 'all' ? 'All time' : period[0].toUpperCase() + period.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="obs-detail-grid">
              <div><span>Attributed total</span><strong>€{(costs?.totals?.total_eur || 0).toFixed(2)}</strong></div>
              <div><span>Processing</span><strong>€{(costs?.totals?.processing_eur || 0).toFixed(2)}</strong></div>
              <div><span>Cold startup</span><strong>€{(costs?.totals?.startup_eur || 0).toFixed(2)}</strong></div>
              <div><span>Warm idle</span><strong>€{(costs?.totals?.idle_eur || 0).toFixed(2)}</strong></div>
              <div><span>Worker billing</span><strong>€{(costs?.totals?.worker_session_eur || 0).toFixed(2)}</strong></div>
              <div><span>Unattributed overhead</span><strong>€{(costs?.totals?.unattributed_eur || 0).toFixed(2)}</strong></div>
            </div>
            <p className="obs-cost-note">Estimated from the configured Scaleway hourly rate. Worker billing reconciles the complete instance lifetime.</p>
          </section>

          {costs?.jobs?.length > 0 && (
            <section className="obs-panel obs-tasks">
              <div className="obs-panel__header">
                <div><h3>Cost by job</h3><p>Highest-cost jobs in this period — select one to see its full timeline.</p></div>
              </div>
              <div className="obs-task-list">
                {costs.jobs.slice(0, 15).map(job => (
                  <button
                    className="obs-task-row"
                    key={job.jobId}
                    onClick={() => openTask({
                      job_id: job.jobId,
                      job_type: job.jobType,
                      language: job.language,
                      status: job.status,
                      updated_at: job.createdAt,
                      cost: job,
                    })}
                  >
                    <span className={`obs-state-dot obs-state-dot--${job.status === 'failed' ? 'failed' : job.status === 'completed' ? 'completed' : 'processing'}`} aria-hidden="true" />
                    <span className="obs-task-main">
                      <strong>{job.jobId}</strong>
                      <small>{job.jobType?.replaceAll('_', ' ')}{job.language ? ` · ${job.language.toUpperCase()}` : ''}</small>
                    </span>
                    <span className="obs-cost-badge">€{Number(job.total_eur || 0).toFixed(2)}</span>
                    <time>{job.createdAt ? new Date(job.createdAt).toLocaleString() : '—'}</time>
                    <span aria-hidden="true">›</span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {costs?.workerSessions?.length > 0 && (
            <section className="obs-panel">
              <div className="obs-panel__header">
                <div><h3>Worker sessions</h3><p>Every GPU instance in this period, including any still running.</p></div>
              </div>
              <div className="obs-worker-list">
                {costs.workerSessions.map(session => (
                  <div className="obs-worker" key={session.worker_id}>
                    <span className={`obs-state-dot obs-state-dot--${session.status === 'running' ? 'processing' : 'completed'}`} aria-hidden="true" />
                    <div>
                      <strong>{session.profile || 'worker'}{session.status === 'running' ? ' · live' : ''}</strong>
                      <small>{session.worker_id} · started {session.started_at ? new Date(session.started_at).toLocaleString() : '—'}</small>
                    </div>
                    <span className="obs-cost-badge">€{Number(session.estimated_cost_eur || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className="obs-capacity-grid">
            <section className="obs-panel">
              <div className="obs-panel__header">
                <div><h3>Worker pool</h3><p>Live Scaleway processing capacity</p></div>
                <strong>{summary?.workers?.total || 0} online</strong>
              </div>
              <div className="obs-worker-list">
                {(summary?.workers?.items || []).length === 0 && <p className="obs-empty">No GPU workers are running.</p>}
                {(summary?.workers?.items || []).map(worker => (
                  <div className="obs-worker" key={worker.workerId}>
                    <span className={`obs-state-dot obs-state-dot--${worker.state}`} aria-hidden="true" />
                    <div>
                      <strong>{worker.profile || 'worker'}</strong>
                      <small>{worker.state === 'idle' ? 'Warm and ready for the next task' : worker.workerId}</small>
                    </div>
                    <span className={`status-badge ${worker.state === 'idle' ? 'status-completed' : 'status-processing'}`}>
                      {worker.state === 'idle' ? 'warm / ready' : worker.state}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            <section className="obs-panel">
              <div className="obs-panel__header">
                <div><h3>Queues</h3><p>Tasks waiting for processing</p></div>
              </div>
              <div className="obs-queue-row"><span>Transcription</span><strong>{summary?.queues?.transcription || 0}</strong></div>
              <div className="obs-queue-row"><span>Translation</span><strong>{summary?.queues?.translation || 0}</strong></div>
            </section>
          </div>

          <section className="obs-panel obs-tasks">
            <div className="obs-panel__header">
              <div>
                <h3>{filter ? `${filter[0].toUpperCase()}${filter.slice(1)} tasks` : 'Recent tasks'}</h3>
                <p>Select a task to inspect its complete timeline.</p>
              </div>
              {filter && <button className="obs-clear" onClick={() => setFilter('')}>Clear filter</button>}
            </div>
            {tasks.length === 0 ? (
              <p className="obs-empty">No tasks match this view. New traces appear after observability is deployed.</p>
            ) : (
              <div className="obs-task-list">
                {tasks.map(task => (
                  <button className="obs-task-row" key={task.job_id} onClick={() => openTask(task)}>
                    <span className={`obs-state-dot obs-state-dot--${task.status}`} aria-hidden="true" />
                    <span className="obs-task-main">
                      <strong>{task.job_id}</strong>
                      <small>{task.job_type?.replaceAll('_', ' ')}{task.language ? ` · ${task.language.toUpperCase()}` : ''}</small>
                    </span>
                    <span className={`status-badge status-${task.status === 'failed' ? 'failed' : task.status === 'completed' ? 'completed' : 'processing'}`}>
                      {task.status}
                    </span>
                    <time>{task.updated_at ? new Date(task.updated_at).toLocaleString() : '—'}</time>
                    <span aria-hidden="true">›</span>
                  </button>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function TaskTraceDetail({ task, detail, loading, onBack }) {
  if (loading) return <div className="analytics-loading"><div className="analytics-spinner" /><p>Loading task trace…</p></div>;
  const events = detail?.events || [];
  const isFailed = task.status === 'failed';
  const formatElapsed = (milliseconds) => {
    if (milliseconds == null || !Number.isFinite(milliseconds)) return '—';
    const seconds = Math.max(0, milliseconds / 1000);
    if (seconds < 1) return `${Math.round(milliseconds)} ms`;
    if (seconds < 60) return `${seconds < 10 ? seconds.toFixed(1) : Math.round(seconds)} sec`;
    const minutes = Math.floor(seconds / 60);
    const remaining = Math.round(seconds % 60);
    if (minutes < 60) return `${minutes} min ${remaining} sec`;
    const hours = Math.floor(minutes / 60);
    return `${hours} hr ${minutes % 60} min`;
  };
  const eventRows = events.map((event, index) => {
    const currentTime = new Date(event.timestamp).getTime();
    const previousTime = index > 0 ? new Date(events[index - 1].timestamp).getTime() : null;
    const sincePreviousMs = previousTime != null && Number.isFinite(currentTime) && Number.isFinite(previousTime)
      ? Math.max(0, currentTime - previousTime)
      : null;
    const explicitDuration = event.event !== 'job.completed' && event.duration_ms != null
      ? Number(event.duration_ms)
      : null;
    return { ...event, sincePreviousMs, displayedDurationMs: explicitDuration ?? sincePreviousMs };
  });
  const firstTimestamp = events[0]?.timestamp ? new Date(events[0].timestamp).getTime() : null;
  const lastTimestamp = events.at(-1)?.timestamp ? new Date(events.at(-1).timestamp).getTime() : null;
  const totalElapsedMs = Number.isFinite(firstTimestamp) && Number.isFinite(lastTimestamp)
    ? Math.max(0, lastTimestamp - firstTimestamp)
    : null;
  const cost = detail?.task?.cost || task.cost || {};
  return (
    <div className="obs-detail">
      <button className="obs-back" onClick={onBack}>← Back to all tasks</button>
      <header className="obs-detail__header">
        <div>
          <span className="obs-health__eyebrow">{task.job_type?.replaceAll('_', ' ')}</span>
          <h2>{isFailed ? 'This task needs attention' : task.status === 'completed' ? 'Task completed successfully' : 'Task is in progress'}</h2>
          <p>{task.job_id}{task.language ? ` · ${task.language.toUpperCase()}` : ''}</p>
        </div>
        <span className={`status-badge status-${isFailed ? 'failed' : task.status === 'completed' ? 'completed' : 'processing'}`}>{task.status}</span>
      </header>

      <div className="obs-detail-grid">
        <div><span>Current stage</span><strong>{task.current_stage?.replaceAll('.', ' ') || 'Unknown'}</strong></div>
        <div><span>Worker</span><strong>{task.worker_id || 'Waiting for worker'}</strong></div>
        <div><span>Attempts</span><strong>{detail?.attempts?.length || 0}</strong></div>
        <div><span>Total elapsed</span><strong>{formatElapsed(totalElapsedMs)}</strong></div>
      </div>

      <section className="obs-panel obs-task-cost">
        <div className="obs-panel__header"><div><h3>Estimated task cost</h3><p>Includes the worker time attributable to this task.</p></div><strong>€{Number(cost.total_eur || 0).toFixed(2)}</strong></div>
        <div className="obs-detail-grid">
          <div><span>Cold startup</span><strong>€{Number(cost.startup_eur || 0).toFixed(2)}</strong><small>{formatElapsed(Number(cost.startup_seconds || 0) * 1000)}</small></div>
          <div><span>Processing</span><strong>€{Number(cost.processing_eur || 0).toFixed(2)}</strong><small>{formatElapsed(Number(cost.processing_seconds || 0) * 1000)}</small></div>
          <div><span>Warm idle</span><strong>€{Number(cost.idle_eur || 0).toFixed(2)}</strong><small>{formatElapsed(Number(cost.idle_seconds || 0) * 1000)}{cost.idle_live ? ' · accruing' : ''}</small></div>
          <div><span>Rate used</span><strong>€{Number(cost.hourly_rate_eur || 0).toFixed(2)}/hr</strong><small>Stored with this job</small></div>
        </div>
      </section>

      <section className="obs-panel">
        <div className="obs-panel__header"><div><h3>Task timeline</h3><p>Each row shows the time spent reaching or completing that step.</p></div></div>
        <ol className="obs-timeline">
          {eventRows.map((event, index) => (
            <li key={event.event_id || index} className={`obs-timeline__item obs-timeline__item--${event.status}`}>
              <span className="obs-timeline__marker" aria-hidden="true" />
              <div className="obs-timeline__content">
                <div><strong>{event.event?.replaceAll('.', ' ')}</strong><time>{new Date(event.timestamp).toLocaleString()}</time></div>
                <p className="obs-step-duration">
                  {index === 0
                    ? 'Trace started'
                    : event.event?.endsWith('.completed') && event.duration_ms != null
                      ? `Step took ${formatElapsed(Number(event.duration_ms))}`
                      : `Reached after ${formatElapsed(event.sincePreviousMs)}`}
                </p>
                {(event.status === 'error' || event.attempt_id) && (
                  <details>
                    <summary>Technical details</summary>
                    <dl>
                      {event.attempt_id && <><dt>Attempt</dt><dd>{event.attempt_id}</dd></>}
                      {event.worker_id && <><dt>Worker</dt><dd>{event.worker_id}</dd></>}
                      {event.attributes?.error_code && <><dt>Error code</dt><dd>{event.attributes.error_code}</dd></>}
                    </dl>
                  </details>
                )}
              </div>
            </li>
          ))}
          {events.length === 0 && <li className="obs-empty">No stage events have been recorded yet.</li>}
        </ol>
      </section>
    </div>
  );
}

function AnalyticsPage() {
  const { getToken } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(toUserMessage(e, 'admin'));
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="analytics-loading">
        <div className="analytics-spinner" />
        <p>Loading analytics…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-error">
        <p>Failed to load analytics: {error}</p>
      </div>
    );
  }

  const audioCount = data.byFileType?.audio ?? 0;
  const videoCount = data.byFileType?.video ?? 0;

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h2 className="analytics-title">Analytics</h2>
        <p className="analytics-sub">All-time transcription metrics for Godstone Tabernacle</p>
      </div>

      <div className="analytics-cards">
        <StatCard label="Transcriptions completed" value={data.totalCompleted.toLocaleString()} />
        <StatCard label="Audio hours processed" value={`${data.totalAudioHours}h`} />
        <StatCard label="Words transcribed" value={data.totalWords.toLocaleString()} />
        <StatCard
          label="Human typing hours saved"
          value={`~${data.humanHoursSaved}h`}
          sub="vs. manual transcription"
        />
      </div>

      <div className="analytics-charts-row">
        <div className="analytics-chart-card analytics-chart-bar">
          <h3 className="chart-title">Transcriptions — last 6 months</h3>
          <BarChartSVG data={data.byMonth} />
        </div>

        <div className="analytics-chart-card analytics-chart-donut">
          <h3 className="chart-title">File type breakdown</h3>
          <div className="donut-wrap">
            <DonutChartSVG audio={audioCount} video={videoCount} />
            <div className="donut-legend">
              <div className="legend-item">
                <span className="legend-dot" style={{ background: '#8B5A2B' }} />
                <span>Audio ({audioCount})</span>
              </div>
              <div className="legend-item">
                <span className="legend-dot" style={{ background: '#D4A574' }} />
                <span>Video ({videoCount})</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {data.recent?.length > 0 && (
        <div className="analytics-recent">
          <h3 className="chart-title">Recent transcriptions</h3>
          <div className="jobs-table-wrapper">
            <table className="jobs-table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Type</th>
                  <th>Duration</th>
                  <th>Processing</th>
                  <th>Words</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((job, i) => {
                  const fname = job.sourceName
                    ? job.sourceName.replace(/^\d{8}_\d{6}_[a-z0-9]+\./, '')
                    : job.jobId;
                  return (
                    <tr key={i}>
                      <td title={job.sourceName}>{fname || job.jobId}</td>
                      <td>
                        <span className={`status-badge ${job.fileType === 'video' ? 'status-processing' : 'status-completed'}`}>
                          {job.fileType}
                        </span>
                      </td>
                      <td>{job.audioDurationMin != null ? `${Math.round(job.audioDurationMin)} min` : '—'}</td>
                      <td>{job.processingTimeMin != null ? `${Math.round(job.processingTimeMin)} min` : '—'}</td>
                      <td>{job.wordCount != null ? job.wordCount.toLocaleString() : '—'}</td>
                      <td>
                        {job.createdAt
                          ? new Date(job.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {data.sampledFrom < data.totalCompleted && (
            <p className="analytics-note">Showing metrics based on the {data.sampledFrom} most recent transcriptions of {data.totalCompleted} total.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// Glossary Panel Component
// ============================================
const GLOSSARY_CATEGORIES = ['Names', 'Places', 'Terminology', 'Scripture', 'Other'];

function GlossaryPanel({ userLangs = [], hasTranscription = true, hasTranslation = false }) {
  const { getToken } = useAuth();
  // Default to 'en' if user has transcription access, else first assigned lang
  const defaultLang = hasTranscription ? 'en' : (userLangs.find(l => l !== 'en') || userLangs[0] || 'en');
  const [glossaryLang, setGlossaryLang] = useState(defaultLang);
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [newTerm, setNewTerm] = useState({ original: '', corrected: '', category: 'Names', mode: 'source_required' });
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const selectAllRef = useRef(null);

  // Route API base depending on selected language
  const apiBase = glossaryLang === 'en'
    ? `${API_BASE}/glossary`
    : `${API_BASE}/glossary/${glossaryLang}`;
  const termsBase = glossaryLang === 'en'
    ? `${API_BASE}/glossary/terms`
    : `${API_BASE}/glossary/${glossaryLang}/terms`;

  const authHeaders = useCallback(async (extra = {}) => {
    const token = await getToken();
    return { Authorization: `Bearer ${token}`, ...extra };
  }, [getToken]);

  const loadTerms = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(apiBase, { headers: await authHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const fresh = await r.json();
      setTerms(fresh);
      const freshIds = new Set(fresh.map(t => t.id));
      setSelectedIds(prev => new Set(Array.from(prev).filter(id => freshIds.has(id))));
    } catch (e) {
      setError(toUserMessage(e, 'default'));
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeaders]);

  useEffect(() => { loadTerms(); }, [loadTerms]);

  // Reset terms list when switching language
  useEffect(() => {
    setTerms([]);
    setEditingId(null);
    setSelectedIds(new Set());
    setNewTerm({ original: '', corrected: '', category: 'Names', mode: 'source_required' });
  }, [glossaryLang]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newTerm.original.trim() || !newTerm.corrected.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(termsBase, {
        method: 'POST',
        headers: await authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(newTerm),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setNewTerm({ original: '', corrected: '', category: 'Names', mode: 'source_required' });
      await loadTerms();
    } catch (e) {
      setError(toUserMessage(e, 'default'));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (term) => {
    setEditingId(term.id);
    setEditDraft({
      original: term.original,
      corrected: term.corrected,
      category: term.category,
      mode: term.mode || 'source_required',
    });
  };

  const saveEdit = async (id) => {
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(`${termsBase}/${id}`, {
        method: 'PUT',
        headers: await authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(editDraft),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setEditingId(null);
      await loadTerms();
    } catch (e) {
      setError(toUserMessage(e, 'default'));
    } finally {
      setSaving(false);
    }
  };

  const deleteTerm = async (id) => {
    if (!window.confirm('Delete this glossary term?')) return;
    setError(null);
    try {
      const r = await fetch(`${termsBase}/${id}`, {
        method: 'DELETE',
        headers: await authHeaders(),
      });
      if (!r.ok && r.status !== 404) throw new Error(`HTTP ${r.status}`);
      await loadTerms();
    } catch (e) {
      setError(toUserMessage(e, 'default'));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev =>
      prev.size === terms.length ? new Set() : new Set(terms.map(t => t.id))
    );
  };

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selectedIds.size > 0 && selectedIds.size < terms.length;
    }
  }, [selectedIds, terms.length]);

  const bulkDeleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected term${ids.length !== 1 ? 's' : ''}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const results = await Promise.all(ids.map(id =>
        fetch(`${termsBase}/${id}`, { method: 'DELETE', headers })
          .then(r => ({ id, ok: r.ok || r.status === 404 }))
          .catch(() => ({ id, ok: false }))
      ));
      const failed = results.filter(r => !r.ok);
      setSelectedIds(new Set());
      await loadTerms();
      if (failed.length > 0) {
        setError(`Deleted ${ids.length - failed.length} of ${ids.length} terms — ${failed.length} failed, please try again.`);
      }
    } catch (e) {
      setError(toUserMessage(e, 'default'));
    } finally {
      setBulkDeleting(false);
    }
  };

  const langLabel = glossaryLang === 'en'
    ? 'English (Transcription)'
    : (LANG_MAP[glossaryLang] || glossaryLang) + ' (Translation)';

  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const fileInputRef = useRef(null);

  const handleImportClick = () => fileInputRef.current?.click();

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await fetch(`${apiBase}/import`, {
        method: 'POST',
        headers: await authHeaders(), // no Content-Type — browser sets multipart boundary
        body: formData,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const result = await r.json();
      setImportResult(result);
      await loadTerms();
    } catch (e) {
      setError(toUserMessage(e, 'default'));
    } finally {
      setImporting(false);
    }
  };

  const downloadFile = async (url, filename) => {
    const r = await fetch(url, { headers: await authHeaders() });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const blob = await r.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  };

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      await downloadFile(`${apiBase}/export`, glossaryLang === 'en' ? 'glossary.csv' : `glossary-${glossaryLang}.csv`);
    } catch (e) {
      setError(toUserMessage(e, 'default'));
    } finally {
      setExporting(false);
    }
  };

  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    setError(null);
    try {
      // CSV opens cleanly in Excel too, so one file covers both — no need
      // to make the person choose a format for a 4-row template.
      await downloadFile(`${API_BASE}/glossary/template?format=csv`, 'glossary-template.csv');
    } catch (e) {
      setError(toUserMessage(e, 'default'));
    } finally {
      setDownloadingTemplate(false);
    }
  };

  return (
    <div className="glossary-panel">
      <div className="glossary-header">
        <h2>Glossary</h2>
        <p className="glossary-desc">
          {glossaryLang === 'en'
            ? 'Corrections for transcription — teaches the transcription tool your vocabulary ahead of time.'
            : `Corrections for ${LANG_MAP[glossaryLang] || glossaryLang} translation — applied during and after AI translation.`}
        </p>

        {/* Language selector */}
        <div className="glossary-lang-bar">
          <label className="glossary-lang-label">Glossary language:</label>
          <select
            className="glossary-lang-select"
            value={glossaryLang}
            onChange={e => setGlossaryLang(e.target.value)}
            disabled={saving}
          >
            {hasTranscription && <option value="en">English (Transcription)</option>}
            {hasTranslation && TRANSLATION_LANGUAGES
              .filter(l => userLangs.includes(l.code))
              .map(l => (
                <option key={l.code} value={l.code}>{l.name} (Translation)</option>
              ))
            }
          </select>
        </div>
      </div>

      {/* Add term form */}
      <div className="glossary-add-card">
        <h3>Add Term</h3>
        <form className="glossary-add-form" onSubmit={handleAdd}>
          <div className="glossary-form-row">
            {glossaryLang !== 'en' && (
              <div className="glossary-form-field glossary-form-cat">
                <label>How to apply it</label>
                <select
                  value={newTerm.mode}
                  onChange={e => setNewTerm(p => ({ ...p, mode: e.target.value }))}
                  disabled={saving}
                >
                  <option value="source_required">English source → required translation</option>
                  <option value="target_correction">Incorrect translation → correction</option>
                </select>
              </div>
            )}
            <div className="glossary-form-field">
              <label>{glossaryLang !== 'en' && newTerm.mode === 'source_required' ? 'English source word or phrase' : 'Incorrect word or phrase'}</label>
              <input
                type="text"
                placeholder="e.g. pastor jon adeyemy"
                value={newTerm.original}
                onChange={e => setNewTerm(p => ({ ...p, original: e.target.value }))}
                disabled={saving}
              />
            </div>
            <div className="glossary-form-arrow">→</div>
            <div className="glossary-form-field">
              <label>{glossaryLang !== 'en' && newTerm.mode === 'source_required' ? 'Required translated term' : 'Approved correction'}</label>
              <input
                type="text"
                placeholder="e.g. Pastor John Adeyemi"
                value={newTerm.corrected}
                onChange={e => setNewTerm(p => ({ ...p, corrected: e.target.value }))}
                disabled={saving}
              />
            </div>
            <div className="glossary-form-field glossary-form-cat">
              <label>Category</label>
              <select
                value={newTerm.category}
                onChange={e => setNewTerm(p => ({ ...p, category: e.target.value }))}
                disabled={saving}
              >
                {GLOSSARY_CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <button type="submit" className="btn-primary glossary-add-btn" disabled={saving || !newTerm.original.trim() || !newTerm.corrected.trim()}>
              {saving ? 'Adding…' : '+ Add'}
            </button>
          </div>
        </form>
      </div>

      {/* Terms table */}
      <div className="glossary-table-section">
        <div className="glossary-table-header">
          <h3>{loading ? 'Loading…' : `${terms.length} term${terms.length !== 1 ? 's' : ''} — ${langLabel}`}</h3>
          <div className="glossary-import-export">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xlsm"
              style={{ display: 'none' }}
              onChange={handleImportFile}
            />
            <button
              type="button"
              className="btn-secondary glossary-io-btn"
              onClick={handleDownloadTemplate}
              disabled={downloadingTemplate}
            >
              {downloadingTemplate ? 'Downloading…' : '📄 Download Template'}
            </button>
            <button
              type="button"
              className="btn-secondary glossary-io-btn"
              onClick={handleImportClick}
              disabled={importing || saving}
            >
              {importing ? 'Importing…' : '⬆ Import CSV/Excel'}
            </button>
            <button
              type="button"
              className="btn-secondary glossary-io-btn"
              onClick={handleExport}
              disabled={exporting || terms.length === 0}
            >
              {exporting ? 'Exporting…' : '⬇ Export CSV'}
            </button>
          </div>
        </div>
        <p className="glossary-io-hint">
          New to importing? Download the template to see the exact columns expected — the same format works for both CSV and Excel files. Any text encoding is accepted (UTF-8, Windows-1252, Windows-1250, etc.) — accented and special characters are detected automatically.
        </p>

        {importResult && (
          <p className="glossary-import-summary">
            Import complete — {importResult.created} added, {importResult.updated} updated
            {importResult.skipped > 0 ? `, ${importResult.skipped} skipped` : ''}.
            <button type="button" className="glossary-import-dismiss" onClick={() => setImportResult(null)}>Dismiss</button>
          </p>
        )}

        {error && <p className="glossary-error">{error}</p>}

        {selectedIds.size > 0 && (
          <div className="glossary-bulk-bar">
            <span>{selectedIds.size} selected</span>
            <button
              type="button"
              className="btn-delete glossary-bulk-delete"
              onClick={bulkDeleteSelected}
              disabled={bulkDeleting}
            >
              {bulkDeleting ? 'Deleting…' : `Delete selected (${selectedIds.size})`}
            </button>
            <button type="button" className="glossary-import-dismiss" onClick={() => setSelectedIds(new Set())}>
              Clear selection
            </button>
          </div>
        )}

        {!loading && terms.length === 0 && (
          <div className="glossary-empty">
            <p>No glossary terms yet. Add your first correction above.</p>
            <p className="glossary-empty-hint">
              Tip: after a transcription, double-click a segment, correct it, then add the
              original + corrected pair here so every future sermon benefits.
            </p>
          </div>
        )}

        {terms.length > 0 && (
          <div className="jobs-table-wrapper">
            <table className="jobs-table glossary-table">
              <thead>
                <tr>
                  <th className="glossary-select-col">
                    <input
                      type="checkbox"
                      ref={selectAllRef}
                      checked={terms.length > 0 && selectedIds.size === terms.length}
                      onChange={toggleSelectAll}
                      aria-label="Select all terms"
                    />
                  </th>
                  <th>What the transcript often says</th>
                  <th>Correct version</th>
                  {glossaryLang !== 'en' && <th>Mode</th>}
                  <th>Category</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {terms.map(term => (
                  <tr key={term.id}>
                    <td className="glossary-select-col">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(term.id)}
                        onChange={() => toggleSelect(term.id)}
                        aria-label={`Select term "${term.original}"`}
                      />
                    </td>
                    {editingId === term.id ? (
                      <>
                        <td>
                          <input
                            className="glossary-inline-input"
                            value={editDraft.original}
                            onChange={e => setEditDraft(p => ({ ...p, original: e.target.value }))}
                          />
                        </td>
                        <td>
                          <input
                            className="glossary-inline-input"
                            value={editDraft.corrected}
                            onChange={e => setEditDraft(p => ({ ...p, corrected: e.target.value }))}
                          />
                        </td>
                        {glossaryLang !== 'en' && (
                          <td>
                            <select
                              className="glossary-inline-select"
                              value={editDraft.mode || 'source_required'}
                              onChange={e => setEditDraft(p => ({ ...p, mode: e.target.value }))}
                            >
                              <option value="source_required">Required translation</option>
                              <option value="target_correction">Post-translation correction</option>
                            </select>
                          </td>
                        )}
                        <td>
                          <select
                            className="glossary-inline-select"
                            value={editDraft.category}
                            onChange={e => setEditDraft(p => ({ ...p, category: e.target.value }))}
                          >
                            {GLOSSARY_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                          </select>
                        </td>
                        <td className="job-actions">
                          <button className="btn-view" onClick={() => saveEdit(term.id)} disabled={saving}>Save</button>
                          <button className="btn-delete" onClick={() => setEditingId(null)} aria-label="Cancel edit">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="glossary-original">{term.original}</td>
                        <td className="glossary-corrected">{term.corrected}</td>
                        {glossaryLang !== 'en' && (
                          <td>
                            <span className="glossary-cat-badge">
                              {(term.mode || 'source_required') === 'target_correction' ? 'Post-translation correction' : 'Required translation'}
                            </span>
                          </td>
                        )}
                        <td><span className="glossary-cat-badge">{term.category}</span></td>
                        <td className="job-actions">
                          <button className="btn-view" onClick={() => startEdit(term)}>Edit</button>
                          <button className="btn-delete" onClick={() => deleteTerm(term.id)} title="Delete" aria-label="Delete term">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            </svg>
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// ============================================
// Access Denied Component
// ============================================
function AccessDenied({ feature }) {
  return (
    <div style={{ textAlign: 'center', padding: '80px 20px', color: '#64748b' }}>
      <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48" style={{ color: '#94a3b8', marginBottom: 16 }}>
        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 4a3 3 0 1 1 0 6 3 3 0 0 1 0-6zm0 14c-2.67 0-8-1.34-8-4v-1c0-2.66 5.33-4 8-4s8 1.34 8 4v1c0 2.66-5.33 4-8 4z"/>
      </svg>
      <h2 style={{ margin: '0 0 8px', color: '#1e293b', fontSize: '1.25rem' }}>Access Restricted</h2>
      <p style={{ margin: 0, maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
        {feature} is only available to authorised users. Contact your administrator to request access.
      </p>
    </div>
  );
}

// ============================================
// Main App Component
// ============================================
function App() {
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();
  const publicMeta        = user?.publicMetadata || {};
  const userRole          = publicMeta.role    || '';
  const userAccess        = publicMeta.access  || '';
  const userGlossaryLangs = publicMeta.glossary_langs || [];
  const isAdmin          = userRole === 'admin' || userRole === 'super_admin';
  const isSuperAdmin     = userRole === 'super_admin';
  const hasTranscription = isAdmin || userAccess === 'transcription' || userAccess === 'full';
  const hasTranslation   = isAdmin || userAccess === 'translation'   || userAccess === 'full';
  const hasAnyAccess     = hasTranscription || hasTranslation;
  const [currentJobId, setCurrentJobId] = useState(null);
  const [appNotice, setAppNotice] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [currentFileName, setCurrentFileName] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [langStreamIdx, setLangStreamIdx] = useState(0);
  const [view, setView] = useState(() => {
    const p = window.location.pathname;
    if (p === '/admin' || p === '/analytics') return isAdmin ? 'analytics' : 'help';
    if (p === '/glossary') return hasAnyAccess ? 'glossary' : 'help';
    if (p === '/translation') return hasTranslation ? 'translation' : 'help';
    if (p === '/feedback') return 'feedback';
    if (p === '/help') return 'help';
    // Default landing based on access level
    if (!hasAnyAccess && !isAdmin) return 'help';
    if (hasTranscription) return 'upload';
    if (hasTranslation) return 'translation';
    return 'help';
  });
  // Separate from `view` (only rendered inside <SignedIn>) — drives what a
  // signed-out visitor sees. Was previously impossible to reach /help signed out.
  const [signedOutView, setSignedOutView] = useState(() =>
    window.location.pathname === '/help' ? 'help' : 'landing'
  );
  const [jobs, setJobs] = useState([]);
  const [jobsHydrated, setJobsHydrated] = useState(false);

  // Translation state
  const [activeEditorTab, setActiveEditorTab] = useState('en');
  const [translationStatusMap, setTranslationStatusMap] = useState({});  // { fr: 'completed' | 'failed' | 'pending' }
  const [translationQueuedAtMap, setTranslationQueuedAtMap] = useState({});
  const [translationDocs, setTranslationDocs] = useState({});             // { fr: <paragraphs doc> }
  const [readyTranscriptions, setReadyTranscriptions] = useState([]);    // jobIds that completed while user was on another tab

  // Poll for translation completion while any language is pending
  useEffect(() => {
    const hasPending = Object.values(translationStatusMap).some(s => s === 'pending');
    if (!hasPending || !currentJobId) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/status/${currentJobId}`);
        if (!res.ok) return;
        const data = await res.json();
        const incoming = data.translations || {};
        setTranslationStatusMap(prev => {
          const updated = { ...prev };
          for (const [lang, status] of Object.entries(incoming)) {
            if (updated[lang] === 'pending') updated[lang] = status;
          }
          return updated;
        });
        if (Object.keys(incoming).length) {
          setJobs(prev => prev.map(job => {
            if (job.jobId !== currentJobId) return job;
            return {
              ...job,
              translationStatus: { ...(job.translationStatus || {}), ...incoming },
            };
          }));
        }
      } catch (_) {}
    }, 15000);
    return () => clearInterval(interval);
  }, [translationStatusMap, currentJobId]);

  const handleTranslationQueued = (langs) => {
    const queuedAt = new Date().toISOString();
    setTranslationStatusMap(prev => {
      const updated = { ...prev };
      for (const lang of langs) updated[lang] = 'pending';
      return updated;
    });
    setTranslationQueuedAtMap(prev => {
      const updated = { ...prev };
      for (const lang of langs) updated[lang] = queuedAt;
      return updated;
    });
    setJobs(prev => prev.map(job => {
      if (job.jobId !== currentJobId) return job;
      const translationStatus = { ...(job.translationStatus || {}) };
      const translationQueuedAt = { ...(job.translationQueuedAt || {}) };
      for (const lang of langs) {
        translationStatus[lang] = 'pending';
        translationQueuedAt[lang] = queuedAt;
      }
      return { ...job, translationStatus, translationQueuedAt };
    }));
  };

  // User-specific storage key
  const storageKey = user ? `whisper-jobs-${user.id}` : 'whisper-jobs';

  // Sync URL bar with view changes
  useEffect(() => {
    if (!user) return; // signed-out URL sync handled separately below
    const target = view === 'analytics' ? '/admin' : view === 'glossary' ? '/glossary' : view === 'help' ? '/help' : view === 'feedback' ? '/feedback' : view === 'translation' ? '/translation' : '/';
    if (window.location.pathname !== target) {
      history.pushState(null, '', target);
    }
  }, [view, user]);

  // Sync URL bar with signed-out view changes
  useEffect(() => {
    if (user) return;
    const target = signedOutView === 'help' ? '/help' : '/';
    if (window.location.pathname !== target) {
      history.pushState(null, '', target);
    }
  }, [signedOutView, user]);

  // Handle browser back/forward
  useEffect(() => {
    const onPop = () => {
      const p = window.location.pathname;
      if (!user) {
        setSignedOutView(p === '/help' ? 'help' : 'landing');
        return;
      }
      if (p === '/admin' || p === '/analytics') setView(isAdmin ? 'analytics' : 'help');
      else if (p === '/glossary') setView(hasAnyAccess ? 'glossary' : 'help');
      else if (p === '/translation') setView(hasTranslation ? 'translation' : 'help');
      else if (p === '/feedback') setView('feedback');
      else if (p === '/help') setView('help');
      else if (hasTranscription) setView('upload');
      else if (hasTranslation) setView('translation');
      else setView('help');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const loadJobs = async () => {
      let localJobs = [];
      const savedJobs = localStorage.getItem(storageKey);
      try {
        const parsed = savedJobs ? JSON.parse(savedJobs) : [];
        localJobs = parsed.map(j =>
          j.jobType === 'pdf' && j.targetLanguages?.length
            ? { ...j, jobType: 'document', fileType: j.fileType || 'pdf' }
            : j
        );
      } catch (e) {
        console.error('Failed to load local jobs:', e);
      }

      let serverJobs = [];
      try {
        const token = await getToken();
        const response = await fetch(`${API_BASE}/jobs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          serverJobs = Array.isArray(data.jobs) ? data.jobs : [];
        }
      } catch (e) {
        console.error('Failed to load synced jobs:', e);
      }

      if (cancelled) return;
      const mergedById = new Map();
      for (const job of [...serverJobs, ...localJobs]) {
        if (job?.jobId) mergedById.set(job.jobId, job);
      }
      const merged = [...mergedById.values()]
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, 100);
      setJobs(merged);
      setJobsHydrated(true);

      // Backfill missing fields for completed jobs (audio + document translation)
      merged.forEach(async (job) => {
          const needsBackfill = job.status === 'completed' && (
            job.audioDuration == null ||
            job.processingTime == null ||
            (isDocumentJob(job) && !job.translationStatus)
          );
          if (!needsBackfill) return;
          try {
            const res = await fetch(`${API_BASE}/status/${job.jobId}`);
            const data = await res.json();
            if (data.status === 'completed') {
              setJobs(prev => prev.map(j => {
                if (j.jobId !== job.jobId) return j;
                return {
                  ...j,
                  status: 'completed',
                  audioDuration: j.audioDuration ?? audioSeconds(data),
                  processingTime: j.processingTime ?? processingSeconds(data),
                  translationStatus: j.translationStatus ?? data.translations ?? {},
                };
              }));
            }
          } catch (e) {
            // ignore — best effort backfill
          }
      });
    };
    loadJobs();
    return () => { cancelled = true; };
  }, [user, storageKey, getToken]);

  useEffect(() => {
    if (!user || !jobsHydrated) return;
    localStorage.setItem(storageKey, JSON.stringify(jobs));
    const timer = window.setTimeout(async () => {
      try {
        const token = await getToken();
        await fetch(`${API_BASE}/jobs`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ jobs }),
        });
      } catch (e) {
        console.error('Failed to sync jobs:', e);
      }
    }, 500);
    return () => window.clearTimeout(timer);
  }, [jobs, user, storageKey, getToken, jobsHydrated]);

  const handlePdfJobQueued = (jobId, originalName, targetLangs) => {
    setCurrentJobId(jobId);
    setCurrentFileName(originalName);
    const newJob = {
      jobId,
      fileName: originalName,
      jobType: 'pdf',
      status: 'queued',
      createdAt: new Date().toISOString(),
      startedAt: Date.now(),
      targetLanguages: targetLangs,
    };
    setJobs(prev => [newJob, ...prev.slice(0, 19)]);
    setView('processing');
  };

  const handleDocumentJobQueued = (jobId, originalName, targetLangs, fileType) => {
    const newJob = {
      jobId,
      fileName: originalName,
      fileType: fileType || 'pdf',
      jobType: 'document',
      status: 'queued',
      createdAt: new Date().toISOString(),
      targetLanguages: targetLangs,
      translationStatus: {},
    };
    setJobs(prev => [newJob, ...prev.slice(0, 19)]);
    // TranslationPanel manages its own internal processing state
  };

  const handleUploadComplete = (jobId, originalName) => {
    setCurrentJobId(jobId);
    setCurrentFileName(originalName);
    const newJob = {
      jobId,
      originalName,
      userId: user?.id,
      status: 'queued',
      createdAt: new Date().toISOString(),
      startedAt: Date.now(),
    };
    setJobs(prev => [newJob, ...prev.slice(0, 19)]);
    setView('processing');
  };

  const handleStatusUpdate = useCallback((statusData) => {
    setJobs(prev => prev.map(job => {
      if (job.jobId !== statusData.jobId) return job;
      return {
        ...job,
        status: statusData.status ?? job.status,
        audioDuration: audioSeconds(statusData) ?? job.audioDuration,
        processingTime: processingSeconds(statusData) ?? job.processingTime,
      };
    }));
  }, []);

  const handleTranscriptReady = useCallback(async (statusData) => {
    const jobId = statusData.jobId;
    try {
      const response = await fetch(`${API_BASE}/transcript/${jobId}`);
      const data = await response.json();
      const currentJob = jobs.find(j => j.jobId === jobId);
      const isPdf = currentJob?.jobType === 'pdf';
      setTranscript(data);
      setLangStreamIdx(0);
      setAudioUrl(isPdf ? null : `${API_BASE}/audio/${jobId}`);
      setActiveEditorTab('en');
      setTranslationDocs({});
      // For PDF jobs, mark pre-selected languages as pending until they appear in status
      const incoming = statusData.translations || {};
      if (isPdf && currentJob?.targetLanguages?.length) {
        const statusMap = { ...incoming };
        for (const lang of currentJob.targetLanguages) {
          if (!statusMap[lang]) statusMap[lang] = 'pending';
        }
        setTranslationStatusMap(statusMap);
      } else {
        setTranslationStatusMap(incoming);
      }

      setJobs(prev => prev.map(job => {
        if (job.jobId !== jobId) return job;
        // Wall-clock elapsed includes queue wait, so it is only a last resort
        // when neither the transcript nor the status endpoint reported a time.
        const elapsed = job.startedAt ? Math.round((Date.now() - job.startedAt) / 1000) : null;
        return {
          ...job,
          status: 'completed',
          audioDuration: audioSeconds(data) ?? audioSeconds(statusData) ?? job.audioDuration ?? null,
          processingTime: processingSeconds(data) ?? processingSeconds(statusData) ?? job.processingTime ?? elapsed ?? null
        };
      }));

      setView('editor');
    } catch (error) {
      console.error('Failed to load transcript:', error);
      setView('upload');
    }
  }, []);

  // ── Background polling ────────────────────────────────────────────────────
  // Keeps all in-progress jobs polled regardless of which view is active.
  // Uses refs so the interval never restarts on view changes.
  const viewRef = useRef(view);
  useEffect(() => { viewRef.current = view; }, [view]);
  const currentJobIdRef = useRef(currentJobId);
  useEffect(() => { currentJobIdRef.current = currentJobId; }, [currentJobId]);
  const jobsRef = useRef(jobs);
  useEffect(() => { jobsRef.current = jobs; }, [jobs]);

  // Clear notification badge whenever the user is looking at any transcription view
  useEffect(() => {
    if (view === 'upload' || view === 'processing' || view === 'editor') {
      setReadyTranscriptions([]);
    }
  }, [view]);

  // Stable interval — started once on mount, reads live values from refs each tick
  useEffect(() => {
    const tick = async () => {
      const pending = jobsRef.current.filter(
        j => ['queued', 'waiting_for_capacity', 'provisioning', 'processing'].includes(j.status)
      );
      if (!pending.length) return;
      for (const job of pending) {
        try {
          const res = await fetch(`${API_BASE}/status/${job.jobId}`);
          if (!res.ok) continue;
          const data = await res.json();
          if (data.status === 'completed' || data.status === 'failed') {
            setJobs(prev => prev.map(j => j.jobId !== job.jobId ? j : {
              ...j,
              status: data.status,
              audioDuration: audioSeconds(data) ?? j.audioDuration,
              processingTime: processingSeconds(data) ?? j.processingTime,
              ...(isDocumentJob(job) && { translationStatus: data.translations ?? j.translationStatus }),
            }));
            if (data.status === 'completed' && !isDocumentJob(job)) {
              const onProcessingView =
                viewRef.current === 'processing' && currentJobIdRef.current === job.jobId;
              if (!onProcessingView) {
                // User is on another tab — surface the badge; StatusPanel handles the in-view case
                setReadyTranscriptions(prev =>
                  prev.includes(job.jobId) ? prev : [job.jobId, ...prev]
                );
              }
            }
          }
        } catch (_) {}
      }
    };
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectJob = async (job) => {
    setCurrentJobId(job.jobId);
    setLangStreamIdx(0);
    setActiveEditorTab('en');
    setTranslationDocs({});
    try {
      const [transcriptRes, statusRes] = await Promise.all([
        fetch(`${API_BASE}/transcript/${job.jobId}`),
        fetch(`${API_BASE}/status/${job.jobId}`),
      ]);
      const data = await transcriptRes.json();
      const statusData = statusRes.ok ? await statusRes.json() : {};
      setTranscript(data);
      setAudioUrl(job.jobType === 'pdf' ? null : `${API_BASE}/audio/${job.jobId}`);
      setTranslationStatusMap({ ...(job.translationStatus || {}), ...(statusData.translations || {}) });
      setTranslationQueuedAtMap(job.translationQueuedAt || {});

      // Backfill duration & processing time from transcript data if missing
      const transcriptAudioSec = audioSeconds(data);
      const transcriptProcSec = processingSeconds(data);
      if (transcriptAudioSec != null || transcriptProcSec != null) {
        setJobs(prev => prev.map(j =>
          j.jobId === job.jobId
            ? {
                ...j,
                audioDuration: j.audioDuration ?? transcriptAudioSec,
                processingTime: j.processingTime ?? transcriptProcSec,
              }
            : j
        ));
      }

      setView('editor');
    } catch (error) {
      setAppNotice(toUserMessage(error, 'default'));
      setTimeout(() => setAppNotice(null), 5000);
    }
  };

  const handleTranslationTabChange = async (lang) => {
    setActiveEditorTab(lang);
    if (lang !== 'en' && !translationDocs[lang]) {
      try {
        const res = await fetch(`${API_BASE}/translation/${currentJobId}/${lang}`);
        if (res.ok) {
          const doc = await res.json();
          setTranslationDocs(prev => ({ ...prev, [lang]: doc }));
        }
      } catch (e) {
        console.error('Failed to load translation:', e);
      }
    }
  };

  const handleSaveTranslation = async (lang, paragraphs) => {
    const res = await fetch(`${API_BASE}/translation/${currentJobId}/${lang}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paragraphs }),
    });
    if (!res.ok) throw new Error('Failed to save translation');
    setTranslationDocs(prev => ({
      ...prev,
      [lang]: { ...prev[lang], paragraphs },
    }));
  };

  const handleDeleteJob = (jobId) => {
    if (!window.confirm('Remove this recording from your history?')) return;
    setJobs(prev => {
      const updated = prev.filter(j => j.jobId !== jobId);
      if (updated.length === 0) {
        localStorage.removeItem(storageKey);
      }
      return updated;
    });
  };

  const handleSaveTranscript = async (paragraphs) => {
    const response = await fetch(`${API_BASE}/transcript/${currentJobId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paragraphs })
    });
    if (!response.ok) throw new Error('Failed to save');
  };

  const resetToUpload = () => {
    setCurrentJobId(null);
    setTranscript(null);
    setAudioUrl(null);
    setActiveEditorTab('en');
    setTranslationStatusMap({});
    setTranslationDocs({});
    setView('upload');
  };

  // Show loading while Clerk initializes
  if (!isLoaded) {
    return (
      <div className="app loading">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Show landing page (or help, if navigated there) when not signed in */}
      <SignedOut>
        {signedOutView === 'help'
          ? <HelpPage onBack={() => setSignedOutView('landing')} />
          : <LandingPage onViewHelp={() => setSignedOutView('help')} />}
      </SignedOut>

      {/* Show main app if signed in */}
      <SignedIn>
        <header className="app-header">
          <div className="logo" onClick={() => setView(hasTranscription ? 'upload' : hasTranslation ? 'translation' : 'help')}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12,2A3,3 0 0,1 15,5V11A3,3 0 0,1 12,14A3,3 0 0,1 9,11V5A3,3 0 0,1 12,2M19,11C19,14.53 16.39,17.44 13,17.93V21H11V17.93C7.61,17.44 5,14.53 5,11H7A5,5 0 0,0 12,16A5,5 0 0,0 17,11H19Z"/>
            </svg>
            <span>GT Sermon Studio</span>
          </div>
          <div className="header-right">
            {hasTranscription && view !== 'upload' && view !== 'processing' && view !== 'editor' && (
              <button className="btn-nav" style={{position:'relative'}} onClick={() => setView('upload')}>
                Transcription
                {readyTranscriptions.length > 0 && (
                  <span style={{
                    position:'absolute', top:3, right:3,
                    width:8, height:8, borderRadius:'50%',
                    background:'#22c55e', display:'block',
                  }} title={`${readyTranscriptions.length} transcription${readyTranscriptions.length > 1 ? 's' : ''} ready`} />
                )}
              </button>
            )}
            {hasTranslation && view !== 'translation' && (
              <button className="btn-nav" onClick={() => setView('translation')}>
                Translation
              </button>
            )}
            {hasAnyAccess && view !== 'glossary' && (
              <button className="btn-nav" onClick={() => setView('glossary')}>
                Glossary
              </button>
            )}
            {isAdmin && view !== 'analytics' && (
              <button className="btn-nav btn-nav--admin" onClick={() => setView('analytics')}>
                Admin Panel
              </button>
            )}
            {view !== 'feedback' && (
              <button className="btn-nav" onClick={() => setView('feedback')}>
                Feedback
              </button>
            )}
            {view !== 'help' && (
              <button className="btn-nav btn-nav--help" onClick={() => setView('help')}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" style={{marginRight: 4}}>
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
                </svg>
                Help
              </button>
            )}
            {hasTranscription && (view === 'processing' || view === 'editor') && (
              <button className="btn-secondary" onClick={resetToUpload}>
                New Transcription
              </button>
            )}
            <UserButton afterSignOutUrl="/" />
          </div>
        </header>

        <main className="app-main">
          {appNotice && (
            <div className="app-notice-banner">
              <span>{appNotice}</span>
              <button className="pending-access-help" onClick={() => setAppNotice(null)}>Dismiss</button>
            </div>
          )}

          {/* Pending-access banner for users without any access */}
          {!hasAnyAccess && !isAdmin && view !== 'help' && (
            <div className="pending-access-banner">
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
              </svg>
              <span>Your account is pending access. Contact your administrator to request a role.</span>
              <button className="pending-access-help" onClick={() => setView('help')}>
                How it works →
              </button>
            </div>
          )}

          {view === 'upload' && (hasTranscription ? (
            <>
              <UploadPanel
                onUploadComplete={handleUploadComplete}
                isUploading={isUploading}
                setIsUploading={setIsUploading}
              />
              <JobsHistory
                jobs={jobs.filter(j => !isDocumentJob(j))}
                onSelectJob={handleSelectJob}
                onDeleteJob={handleDeleteJob}
                currentJobId={currentJobId}
              />
            </>
          ) : <AccessDenied feature="Transcription" />)}

          {view === 'processing' && (
            <StatusPanel
              jobId={currentJobId}
              fileName={currentFileName}
              onTranscriptReady={handleTranscriptReady}
              onStatusUpdate={handleStatusUpdate}
              onStartOver={resetToUpload}
            />
          )}

          {view === 'translation' && (hasTranslation ? (
            <TranslationPanel
              onJobQueued={handleDocumentJobQueued}
              jobs={jobs.filter(isDocumentJob)}
              onDeleteJob={handleDeleteJob}
              userGlossaryLangs={userGlossaryLangs}
              onUpdateJob={(jobId, updates) => setJobs(prev => prev.map(j => {
                if (j.jobId !== jobId) return j;
                const merged = {...j, ...updates};
                if (updates.status === 'completed' && merged.processingTime == null) {
                  // Seconds, to match every other processingTime in the app.
                  // startedAt excludes queue wait; createdAt is the fallback.
                  const origin = j.startedAt ?? (j.createdAt ? new Date(j.createdAt).getTime() : null);
                  if (origin) merged.processingTime = Math.round((Date.now() - origin) / 1000);
                }
                return merged;
              }))}
            />
          ) : <AccessDenied feature="Translation" />)}

          {view === 'analytics' && (isAdmin ? <AdminPanel isSuperAdmin={isSuperAdmin} /> : <AccessDenied feature="Admin Panel" />)}

          {view === 'glossary' && (hasAnyAccess ? (
            <GlossaryPanel
              userLangs={userGlossaryLangs}
              hasTranscription={hasTranscription}
              hasTranslation={hasTranslation}
            />
          ) : <AccessDenied feature="Glossary" />)}

          {view === 'help' && (
            <HelpPage
              isPendingAccess={!hasAnyAccess && !isAdmin}
              adminEmail=""
            />
          )}

          {view === 'feedback' && (
            <FeedbackPage onBack={() => setView(hasTranscription ? 'upload' : hasTranslation ? 'translation' : 'help')} />
          )}

          {view === 'editor' && transcript && (() => {
            const isBoth = transcript.mode === 'both' && Array.isArray(transcript.streams);
            const completedTranslations = Object.entries(translationStatusMap)
              .filter(([, s]) => s === 'completed')
              .map(([lang]) => lang);
            const pendingTranslations = Object.entries(translationStatusMap)
              .filter(([, s]) => s === 'pending')
              .map(([lang]) => lang);
            const hasAnyTabs = isBoth || completedTranslations.length > 0 || pendingTranslations.length > 0;

            const currentJob = jobs.find(j => j.jobId === currentJobId);
            const isPdfJob = currentJob?.jobType === 'pdf';

            let activeTranscript;
            let onSaveActive;
            if (activeEditorTab === 'en') {
              activeTranscript = isBoth ? transcript.streams[langStreamIdx] : transcript;
              onSaveActive = handleSaveTranscript;
            } else {
              activeTranscript = translationDocs[activeEditorTab] || null;
              onSaveActive = (paragraphs) => handleSaveTranslation(activeEditorTab, paragraphs);
            }

            return (
              <div className="editor-layout">
                {hasAnyTabs && (
                  <div className="lang-stream-tabs">
                    {isBoth ? (
                      transcript.streams.map((stream, idx) => (
                        <button
                          key={idx}
                          className={`lang-stream-tab${activeEditorTab === 'en' && langStreamIdx === idx ? ' active' : ''}`}
                          onClick={() => { setActiveEditorTab('en'); setLangStreamIdx(idx); }}
                        >
                          {stream._lang ? stream._lang.toUpperCase() : `Stream ${idx + 1}`}
                        </button>
                      ))
                    ) : (
                      <button
                        className={`lang-stream-tab${activeEditorTab === 'en' ? ' active' : ''}`}
                        onClick={() => setActiveEditorTab('en')}
                      >
                        {isPdfJob ? 'English (Source)' : 'English (Original)'}
                      </button>
                    )}
                    {completedTranslations.map(lang => (
                      <button
                        key={lang}
                        className={`lang-stream-tab${activeEditorTab === lang ? ' active' : ''}`}
                        onClick={() => handleTranslationTabChange(lang)}
                      >
                        {LANG_MAP[lang] || lang.toUpperCase()}
                      </button>
                    ))}
                    {pendingTranslations.map(lang => (
                      <button key={lang} className="lang-stream-tab lang-stream-tab--pending" disabled>
                        {LANG_MAP[lang] || lang.toUpperCase()} …
                      </button>
                    ))}
                  </div>
                )}

                {activeTranscript ? (
                  <>
                    <TranscriptEditor
                      key={activeEditorTab}
                      transcript={activeTranscript}
                      audioUrl={audioUrl}
                      onSave={onSaveActive}
                      onAudioDurationLoaded={(dur) => {
                        setJobs(prev => prev.map(j =>
                          j.jobId === currentJobId && !j.audioDuration
                            ? { ...j, audioDuration: Math.round(dur) }
                            : j
                        ));
                      }}
                    />
                    <aside className="editor-sidebar">
                      <ExportOptions transcript={activeTranscript} jobId={currentJobId} onBackToDashboard={resetToUpload} />
                      <TranslatePanel
                        jobId={currentJobId}
                        translationStatusMap={translationStatusMap}
                        translationQueuedAtMap={translationQueuedAtMap}
                        onTranslationQueued={handleTranslationQueued}
                        userGlossaryLangs={userGlossaryLangs}
                      />
                    </aside>
                  </>
                ) : (
                  <div className="translation-loading">
                    <div className="analytics-spinner" />
                    <p>Loading {LANG_MAP[activeEditorTab] || activeEditorTab} translation…</p>
                  </div>
                )}
              </div>
            );
          })()}
        </main>

        <footer className="app-footer">
          <span>GT Sermon Studio — Godstone Tabernacle</span>
        </footer>
      </SignedIn>
    </div>
  );
}

export default App;
